<template>
  <div id="app" class="min-h-screen flex flex-col md:flex-row antialiased selection:bg-sky-100 selection:text-sky-900 w-full overflow-x-hidden">
    <aside id="sidebarNav" class="w-full md:w-72 bg-white text-slate-700 flex-shrink-0 flex flex-col border-r border-slate-200 sticky top-0 z-40 md:h-screen shadow-xs">
        
        <!-- Sidebar Header (Profile Settings Trigger) -->
        <div class="p-4 border-b border-slate-100 flex items-center justify-between bg-sky-50/40">
            <div @click="openModalGuarded('siteSettingsModal')" class="flex items-center space-x-3 cursor-pointer group hover:opacity-90 transition" title="点击修改个人与网站设置">
                <div id="sidebarAvatarContainer" class="w-10 h-10 rounded-2xl bg-sky-500 flex items-center justify-center text-white shadow-md shadow-sky-500/20 overflow-hidden relative group-hover:scale-105 transition">
                    <i id="sidebarAvatarIcon" ref="sidebarAvatarIcon" class="fa-solid fa-baby text-xl"></i>
                    <img id="sidebarAvatarImg" ref="sidebarAvatarImg" src="" class="w-full h-full object-cover hidden" alt="Avatar">
                </div>
                <div class="min-w-0">
                    <div class="flex items-center space-x-1">
                        <h1 id="sidebarSiteTitle" ref="sidebarSiteTitle" class="text-sm font-extrabold text-slate-800 tracking-wide truncate">王秉宣的成长纪事</h1>
                        <i class="fa-solid fa-gear text-[10px] text-slate-400 group-hover:text-sky-600 transition"></i>
                    </div>
                    <span id="sidebarDadStatus" ref="sidebarDadStatus" class="inline-block bg-sky-100 text-sky-800 text-[10px] px-2 py-0.5 rounded-full border border-sky-200 font-semibold truncate bkleft">奶爸备养中</span>
                </div>
            </div>
            <button @click="toggleMobileNav()" class="md:hidden text-slate-500 hover:text-slate-800 p-2 focus:outline-none">
                <i class="fa-solid fa-bars text-lg"></i>
            </button>
        </div>

        <div class="p-3.5 border-b border-slate-200 bg-gradient-to-br from-amber-50/90 via-orange-50/60 to-rose-50/80">
            <div class="bg-white/80 backdrop-blur-xs border border-amber-200/80 rounded-2xl p-3.5 space-y-2.5 shadow-2xs">
                <div class="flex items-center justify-between">
                    <span class="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                        <i class="fa-solid fa-hourglass-half text-amber-500"></i>
                        <span>{{this.userInfo.chlidName}}诞生倒计时</span>
                    </span>
                    <span id="dueDateTag" ref="dueDateTag" class="bg-rose-100 text-rose-700 text-[10px] px-2 py-0.5 rounded-full font-extrabold border border-rose-200">2027-06-15</span>
                </div>

                <div class="flex items-baseline justify-between pt-1">
                    <div class="text-slate-600 text-xs">
                        剩余 <span id="countdownDaysLarge" ref="countdownDaysLarge" class="text-2xl font-black text-rose-600 font-mono tracking-tight">0</span> 天
                    </div>
                    <div class="text-right">
                        <span id="gestationalWeek" ref="gestationalWeek" class="text-xs font-bold text-slate-700">孕中期 · 18周+4天</span>
                    </div>
                </div>

                <!-- Gestational Progress Bar -->
                <div class="space-y-1">
                    <div class="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                        <div id="gestationalProgressBar" ref="gestationalProgressBar" class="bg-gradient-to-r from-amber-400 to-rose-500 h-2 rounded-full transition-all duration-500" style="width: 45%;"></div>
                    </div>
                    <div class="flex justify-between text-[10px] text-slate-400 font-mono">
                        <span>孕早期</span>
                        <span>孕中期</span>
                        <span>孕晚期</span>
                    </div>
                </div>

                <!-- EMBRYO TRANSFER & DUE DATE CALCULATOR LAUNCHER -->
                <button @click="toggleTransferCalc()" class="w-full mt-1 py-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-900 rounded-lg text-[11px] font-bold transition flex items-center justify-center space-x-1">
                    <i class="fa-solid fa-calculator text-amber-600"></i>
                    <span>⚙️ 计算/校准预产期 (胚胎移植/末次月经)</span>
                </button>

                <!-- Embedded Transfer Calculator Drawer -->
                <div id="transferCalcDrawer" ref="transferCalcDrawer" class="hidden bg-white p-3 rounded-xl border border-amber-200 space-y-2 text-xs text-slate-700 mt-2">
                    <div class="font-bold text-amber-900 flex justify-between items-center text-[11px]">
                        <span>🧪 预产期推算算法</span>
                        <button @click="toggleTransferCalc()" class="text-slate-400 hover:text-slate-600"><i class="fa-solid fa-xmark"></i></button>
                    </div>
                    <div>
                        <label class="block text-[10px] text-slate-500 mb-0.5 txtleft">选择推算依据</label>
                        <select id="calcTypeSelect" ref="calcTypeSelect" class="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-slate-50">
                            <option v-for="item in pregnancyList" :key="item.id" :value="item.id">{{ item.dictName }}</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-[10px] text-slate-500 mb-0.5 txtleft">选择移植 / 月经日期</label>
                        <input type="date" id="calcDateInput" ref="calcDateInput" @click="onDatePickerClick($event)" class="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-slate-50 cursor-pointer">
                    </div>
                    <button @click="calculateAndSaveDueDate()" class="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-1.5 rounded-lg transition text-xs">
                        更新并保存最新预产期
                    </button>
                </div>
            </div>
        </div>

        <nav id="navMenuItems" ref="navMenuItems" class="hidden md:flex flex-1 flex-col p-3 space-y-1 overflow-y-auto custom-scrollbar text-xs font-medium">

            <div class="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 pt-2 pb-1 txtleft">日常护理与数据记录</div>

            <button @click="switchTab('tracker')" id="nav-btn-tracker" ref="nav-btn-tracker" class="nav-item active w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-bolt text-amber-500 w-5 text-center"></i>
                <span>日常护理与生长速记</span>
            </button>

            <button @click="switchTab('fetal')" id="nav-btn-fetal" ref="nav-btn-fetal" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-person-pregnant text-rose-500 w-5 text-center"></i>
                <span>孕期胎儿数据与产检</span>
            </button>

            <button @click="switchTab('charts')" id="nav-btn-charts" ref="nav-btn-charts" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-chart-line text-purple-500 w-5 text-center"></i>
                <span>WHO生长曲线与数据表</span>
            </button>

            <div class="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 pt-3 pb-1 txtleft">育儿知识与深度科学</div>

            <button @click="switchTab('diary')" id="nav-btn-diary" ref="nav-btn-diary" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-book-bookmark text-indigo-500 w-5 text-center"></i>
                <span>时光成长日记 (年月归档)</span>
            </button>

            <button @click="switchTab('album')" id="nav-btn-album" ref="nav-btn-album" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-images text-teal-500 w-5 text-center"></i>
                <span>{{this.userInfo.chlidName}}时光成长相册</span>
            </button>

            <button @click="switchTab('guide')" id="nav-btn-guide" ref="nav-btn-guide" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-utensils text-emerald-500 w-5 text-center"></i>
                <span>0-3岁科学喂养&4个月辅食计划</span>
            </button>

            <button @click="switchTab('attachment')" id="nav-btn-attachment" ref="nav-btn-attachment" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-heart text-rose-500 w-5 text-center"></i>
                <span>回避型奶爸的安全型依恋重塑与打卡</span>
            </button>

            <button @click="switchTab('motor')" id="nav-btn-motor" ref="nav-btn-motor" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-person-running text-cyan-600 w-5 text-center"></i>
                <span>0-3岁运动与神经肌肉发育</span>
            </button>

            <button @click="switchTab('brain')" id="nav-btn-brain" ref="nav-btn-brain" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-brain text-pink-500 w-5 text-center"></i>
                <span>0-3岁大脑脑科学 (海马体/前额叶)</span>
            </button>

            <button @click="switchTab('emotion')" id="nav-btn-emotion" ref="nav-btn-emotion" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-heart-pulse text-rose-500 w-5 text-center"></i>
                <span>0-10岁儿童情绪理解深度演进</span>
            </button>

            <div class="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 pt-3 pb-1 txtleft">随感</div>

            <button @click="switchTab('mood')" id="nav-btn-mood" ref="nav-btn-mood" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-comment-dots text-amber-500 w-5 text-center"></i>
                <span>奶爸心情微语</span>
            </button>
        </nav>

        <!-- Sidebar Footer -->
        <div class="p-3 border-t border-slate-100 bg-slate-50 space-y-2">
            <button @click="openModalGuarded('quickAddModal')" class="w-full bg-sky-600 hover:bg-sky-700 text-white text-xs py-2.5 rounded-xl font-semibold shadow-md transition flex items-center justify-center space-x-2">
                <i class="fa-solid fa-plus"></i>
                <span>写新日记 / 上传多媒体</span>
            </button>
        </div>
    </aside>

    <main class="flex-1 flex flex-col min-w-0 h-screen overflow-y-auto custom-scrollbar bg-slate-50 w-full max-w-full">

        <!-- Top Sticky Header -->
        <header class="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 px-3 sm:px-6 py-3 flex justify-between items-center shadow-2xs w-full">
            <div class="min-w-0 flex-1 pr-2">
                <h2 id="currentTabTitle" ref="currentTabTitle" class="text-sm sm:text-base md:text-lg font-extrabold text-slate-800 flex items-center gap-2 truncate">
                    <span>⚡ 日常护理与生长速记中心</span>
                </h2>
                <p id="currentTabSubtitle" ref="currentTabSubtitle" class="text-xs text-slate-500 hidden md:block truncate bkleft">一键快速登记宝宝喂养、睡眠、换尿布、生长指标、皮肤护理、用药执行、疫苗与萌牙情况。</p>
            </div>
            <button @click="openModalGuarded('quickAddModal')" class="bg-sky-600 hover:bg-sky-700 text-white text-xs px-3 py-1.5 rounded-lg font-semibold shadow-xs transition flex items-center space-x-1 shrink-0">
                <i class="fa-solid fa-plus"></i>
                <span class="hidden sm:inline">写日记</span>
            </button>
        </header>

        <!-- Dynamic Content Body -->
        <div class="p-3 sm:p-6 space-y-6 flex-1 w-full max-w-7xl mx-auto">

            <!-- ================= TAB 1: 日常护理与生长速记 ================= -->
            <section id="tab-tracker" ref="tab-tracker" class="tab-content space-y-6">
                <!-- Quick Tips Card -->
                <div class="bg-gradient-to-r from-sky-500/10 via-sky-400/5 to-transparent border border-sky-200 rounded-2xl p-4 flex items-center space-x-3">
                    <div class="w-10 h-10 rounded-xl bg-sky-500 text-white flex items-center justify-center text-lg flex-shrink-0">
                        <i class="fa-solid fa-lightbulb"></i>
                    </div>
                    <div class="text-xs text-slate-600">
                        <strong class="text-sky-900 font-bold block txtleft">奶爸一键打卡秘诀</strong>
                        点击下方对应功能板块即可秒级记录。所有历史数据会自动聚合至下方的时间轴数据库，方便就医咨询或日常复盘。
                    </div>
                </div>

                <!-- Grid of Quick Action Cards (All buttons aligned at bottom row) -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                    <!-- Card 1: 喂奶速记 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">🍼 喂奶速记</span>
                                <span class="text-[10px] bg-sky-100 text-sky-700 px-2 py-0.5 rounded font-medium">喂养</span>
                            </div>
                            <div class="space-y-2">
                                <div class="grid grid-cols-3 gap-1.5 text-xs">
                                    <button type="button" @click="$refs.customFeedMl.value = 60" class="py-1.5 bg-slate-50 hover:bg-sky-50 border border-slate-200 hover:border-sky-300 rounded-lg font-medium transition">60 ml</button>
                                    <button type="button" @click="$refs.customFeedMl.value = 90" class="py-1.5 bg-slate-50 hover:bg-sky-50 border border-slate-200 hover:border-sky-300 rounded-lg font-medium transition">90 ml</button>
                                    <button type="button" @click="$refs.customFeedMl.value = 120" class="py-1.5 bg-slate-50 hover:bg-sky-50 border border-slate-200 hover:border-sky-300 rounded-lg font-medium transition">120 ml</button>
                                </div>
                                <input type="number" id="customFeedMl" ref="customFeedMl" placeholder="奶量 (ml)，如 90" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-sky-500 focus:outline-none">
                            </div>
                        </div>
                        <button @click="quickLogFeed()" class="w-full bg-sky-600 hover:bg-sky-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">保存喂奶记录</button>
                    </div>

                    <!-- Card 2: 睡眠记录 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">😴 睡眠记录</span>
                                <span class="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-medium">作息</span>
                            </div>
                            <div class="space-y-2">
                                <select id="sleepTypeSelect" ref="sleepTypeSelect" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500">
                                    <template v-if="dailyList.length > 1">
                                        <option v-for="item in dailyList[1].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                    </template>
                                    <template v-else>
                                        <option value="🌙 开始入睡">🌙 开始入睡</option>
                                        <option value="☀️ 小睡醒来">☀️ 小睡醒来</option>
                                        <option value="🌌 夜间沉睡">🌌 夜间沉睡</option>
                                        <option value="🥺 夜间醒啼哭">🥺 夜间醒啼哭</option>
                                    </template>
                                </select>
                                <input type="text" id="sleepNoteInput" ref="sleepNoteInput" placeholder="睡眠备注 (如：睡得安稳/易惊醒)" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 focus:outline-none">
                            </div>
                        </div>
                        <button @click="quickLogSleep()" class="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">保存睡眠记录</button>
                    </div>

                    <!-- Card 3: 换尿布 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">💩 换尿布</span>
                                <span class="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-medium">排泄</span>
                            </div>
                            <div class="space-y-2">
                                <select id="diaperTypeSelect" ref="diaperTypeSelect" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-amber-500">
                                    <template v-if="dailyList.length > 2">
                                        <option v-for="item in dailyList[2].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                    </template>
                                    <template v-else>
                                        <option value="💧 仅尿湿">💧 仅尿湿</option>
                                        <option value="💩 有便便">💩 有便便</option>
                                        <option value="🔗 尿便混合">🔗 尿便混合</option>
                                        <option value="✨ 检查干爽">✨ 检查干爽</option>
                                        <option value="💩 有便便">💩 有便便</option>
                                        <option value="🔗 尿便混合">🔗 尿便混合</option>
                                        <option value="✨ 干爽无换">✨ 检查干爽</option>
                                    </template>
                                </select>
                                <input type="text" id="diaperNoteInput" ref="diaperNoteInput" placeholder="便便性状 (如：黄金糊状/略稀)" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-amber-500 focus:outline-none">
                            </div>
                        </div>
                        <button @click="quickLogDiaper()" class="w-full bg-amber-600 hover:bg-amber-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">保存排泄记录</button>
                    </div>

                    <!-- Card 4: 生长数据测定 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">📏 生长数据测定</span>
                                <span class="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-medium">指标</span>
                            </div>
                            <div class="grid grid-cols-3 gap-2">
                                <input type="number" id="weightInput" ref="weightInput" placeholder="体重kg" class="px-2 py-1.5 border border-slate-300 rounded-lg text-xs">
                                <input type="number" id="heightInput" ref="heightInput" placeholder="身高cm" class="px-2 py-1.5 border border-slate-300 rounded-lg text-xs">
                                <input type="number" id="headInput" ref="headInput" placeholder="头围cm" class="px-2 py-1.5 border border-slate-300 rounded-lg text-xs">
                            </div>
                        </div>
                        <button @click="logGrowth()" class="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">保存测量数值</button>
                    </div>

                    <!-- Card 5: 皮肤护理与洗澡 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">🧴 皮肤护理与洗澡</span>
                                <span class="text-[10px] bg-rose-100 text-rose-700 px-2 py-0.5 rounded font-medium">护肤</span>
                            </div>
                            <div class="space-y-2">
                                <select id="skinTypeSelect" ref="skinTypeSelect" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-rose-500">
                                    <template v-if="dailyList.length > 4">
                                        <option v-for="item in dailyList[4].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                    </template>
                                    <template v-else>
                                        <option value="🛋️ 洗澡与抚触">🛋️ 洗澡与抚触</option>
                                        <option value="🧴 涂面霜/护臀膏">rops 涂面霜/护臀膏</option>
                                        <option value="🩺 湿疹敷药护理">🩺 湿疹敷药护理</option>
                                        <option value=" 💆 婴儿抚触按摩">💆 婴儿抚触按摩</option>
                                    </template>
                                </select>
                                <input type="text" id="skinNoteInput" ref="skinNoteInput" placeholder="皮肤状况 (如：脸颊微红/湿疹护理)" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-rose-500 focus:outline-none">
                            </div>
                        </div>
                        <button @click="quickLogSkin()" class="w-full bg-rose-600 hover:bg-rose-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">保存护肤记录</button>
                    </div>

                    <!-- Card 6: 用药打卡 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">💊 用药打卡</span>
                                <span class="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium">健康</span>
                            </div>
                            <div class="space-y-2">
                                <select id="medSelect" ref="medSelect" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-purple-500">
                                    <template v-if="dailyList.length > 5">
                                        <option v-for="item in dailyList[5].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                    </template>
                                    <template v-else>
                                        <option value="☀️维D3 (400IU)">☀️ 维D3 (400IU)</option>
                                        <option value="🦠 益生菌">🦠 益生菌</option>
                                        <option value="💊 维AD胶囊">💊 维AD胶囊</option>
                                        <option value="✏️ 其他药品/补充剂">✏️ 其他药品/补充剂</option>
                                    </template>
                                </select>
                                <input type="text" id="customMedInput" ref="customMedInput" placeholder="其他药品/剂量 (如美林 2.5ml)" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-purple-500 focus:outline-none">
                            </div>
                        </div>
                        <button @click="quickLogMed()" class="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">完成用药打卡</button>
                    </div>

                    <!-- Card 7: 💉 深圳市卫健委指定疫苗接种计划记录 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">💉 疫苗接种记录</span>
                                <span class="text-[10px] bg-sky-100 text-sky-800 px-2 py-0.5 rounded font-medium">深圳卫健委标准</span>
                            </div>
                            <div>
                                <select id="vaccineSelect" ref="vaccineSelect" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-sky-500">
                                    <template v-if="dailyList.length > 6">
                                        <option v-for="item in dailyList[6].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                    </template>
                                    <template v-else>
                                        <option value="注射：乙肝疫苗第一针 (出生时刻)">注射：乙肝疫苗第一针 (出生时刻)</option>
                                        <option value="接种：卡介苗 (出生24小时内)">接种：卡介苗 (出生24小时内)</option>
                                        <option value="注射：乙肝疫苗第二针 (1月龄)">注射：乙肝疫苗第二针 (1月龄)</option>
                                        <option value="注射：脊灰灭活疫苗第一针 IPV (2月龄)">注射：脊灰灭活疫苗第一针 IPV (2月龄)</option>
                                        <option value="注射：脊灰灭活疫苗第二针 IPV (3月龄)">注射：脊灰灭活疫苗第二针 IPV (3月龄)</option>
                                        <option value="注射：百白破疫苗第一针 (3月龄)">注射：百白破疫苗第一针 (3月龄)</option>
                                        <option value="注射：百白破疫苗第二针 (4月龄)">注射：百白破疫苗第二针 (4月龄)</option>
                                        <option value="注射：13价肺炎球菌疫苗 (自费推荐)">注射：13价肺炎球菌疫苗 (自费推荐)</option>
                                        <option value="注射：五联疫苗 (自费替代)">注射：五联疫苗 (自费替代)</option>
                                        <option value="注射：乙肝疫苗第三针 (6月龄)">注射：乙肝疫苗第三针 (6月龄)</option>
                                        <option value="注射：流脑A群疫苗第一针 (6月龄)">注射：流脑A群疫苗第一针 (6月龄)</option>
                                        <option value="注射：麻腮风疫苗第一针 MMR (8月龄)">注射：麻腮风疫苗第一针 MMR (8月龄)</option>
                                        <option value="注射：乙脑减毒活疫苗第一针 (8月龄)">注射：乙脑减毒活疫苗第一针 (8月龄)</option>
                                    </template>
                                </select>
                            </div>
                        </div>
                        <button @click="quickLogVaccine()" class="w-full bg-sky-600 hover:bg-sky-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">完成疫苗登记</button>
                    </div>

                    <!-- Card 8: 🦷 出牙记录 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">🦷 出牙记录</span>
                                <span class="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-medium">萌牙</span>
                            </div>
                            <div>
                                <select id="teethPositionSelect" ref="teethPositionSelect" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs">
                                    <template v-if="dailyList.length > 7">
                                        <option v-for="item in dailyList[7].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                    </template>
                                    <template v-else>
                                        <option value="下门牙左/右 (通常最早6月萌出)">下门牙左/右 (通常最早6月萌出)</option>
                                        <option value="上门牙左/右 (通常8-10月萌出)">上门牙左/右 (通常8-10月萌出)</option>
                                        <option value="侧门牙 (通常9-13月萌出)">侧门牙 (通常9-13月萌出)</option>
                                        <option value="第一磨牙 (通常13-19月萌出)">第一磨牙 (通常13-19月萌出)</option>
                                        <option value="犬齿/尖牙 (通常16-23月萌出)">犬齿/尖牙 (通常16-23月萌出)</option>
                                    </template>
                                </select>
                            </div>
                        </div>
                        <button @click="quickLogTeeth()" class="w-full bg-amber-600 hover:bg-amber-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">登记萌牙时刻</button>
                    </div>

                    <!-- Card 9: 📝 自定义速记事项 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">📝 自定义速记事项</span>
                                <span class="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-medium">其他</span>
                            </div>
                            <div class="space-y-2">
                                <input type="text" id="customEventName" ref="customEventName" placeholder="事项名称 (如: 趴卧抬头练习/听早教音乐)" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-sky-500">
                                <input type="text" id="customEventNote" ref="customEventNote" placeholder="详细说明或感受" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-sky-500">
                            </div>
                        </div>
                        <button @click="quickLogCustomEvent()" class="w-full bg-slate-800 hover:bg-slate-900 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">添加自定义记录</button>
                    </div>

                    <!-- Card 10: ❤️ 安全依恋亲子互动打卡 -->
                    <div class="bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-rose-900 flex items-center gap-1.5">❤️ 安全依恋亲子互动打卡</span>
                                <span class="text-[10px] bg-rose-100 text-rose-700 px-2 py-0.5 rounded font-bold">依恋重塑</span>
                            </div>
                            <select id="attachmentInteractionSelect" ref="attachmentInteractionSelect" class="w-full px-2.5 py-1.5 border border-rose-200 rounded-lg text-xs bg-white text-slate-800">
                                <template v-if="dailyList.length > 9">
                                    <option v-for="item in dailyList[9].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                </template>
                                <template v-else>
                                    <option value="完成15分钟专注皮肤接触/婴儿抚触">完成15分钟专注皮肤接触/婴儿抚触</option>
                                    <option value="哭闹时3秒内温和回应与拥抱">哭闹时3秒内温和回应与拥抱</option>
                                    <option value="完成离别告别仪式 (眼神凝视+承诺归来)">完成离别告别仪式 (眼神凝视+承诺归来)</option>
                                    <option value="进行情绪接纳与命名 (帮孩子说出心理感受)">进行情绪接纳与命名 (帮孩子说出心理感受)</option>
                                    <option value="睡前10分钟无手机专注共读与互动">睡前10分钟无手机专注共读与互动</option>
                                    <option value="口头表达爱意 (告诉秉宣: 爸爸很爱你/为你骄傲)">口头表达爱意 (告诉秉宣: 爸爸很爱你/为你骄傲)</option>
                                </template>
                            </select>
                        </div>
                        <button @click="quickLogAttachment()" class="w-full bg-rose-600 hover:bg-rose-700 text-white py-2 rounded-lg text-xs font-semibold transition shadow-xs mt-2">完成依恋互动打卡</button>
                    </div>

                </div>

                <!-- Tracker History Log Database Box -->
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-4">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <h3 class="text-xs sm:text-sm font-bold text-slate-800 flex items-center gap-2">
                            <span>📜 {{this.userInfo.chlidName}}的日常护理与成长打卡数据库</span>
                        </h3>
                        <div class="flex space-x-2 text-xs">
                            <button @click="filterTrackerLogs('ALL')" class="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md font-medium">全部显示</button>
                            <!-- <button @click="clearTrackerLogs()" class="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-md font-medium">清空当前演示</button> -->
                        </div>
                    </div>

                    <div id="trackerHistoryList" ref="trackerHistoryList" @click="delegateClick" class="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
                        <!-- Dynamic rendered tracker logs -->
                    </div>
                </div>

            </section>

            <!-- ================= TAB 2: 孕期胎儿数据与产检记录 ================= -->
            <section id="tab-fetal" ref="tab-fetal" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-5">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-person-pregnant text-rose-500"></i>
                                <span>孕期胎儿数据与产检档案</span>
                            </h3>
                            <p class="text-xs text-slate-500">详细记录{{this.userInfo.chlidName}}在妈妈肚子里每周的B超数据、胎心率、骨骼发育与医生指导医嘱。</p>
                        </div>
                        <span class="bg-rose-100 text-rose-800 text-xs px-2.5 py-1 rounded-full font-semibold">产检档案</span>
                    </div>

                    <!-- Form to add fetal data -->
                    <form id="fetalDataForm" ref="fetalDataForm" @submit.prevent="saveFetalCheckup($event)" class="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-3 text-xs">
                        <div class="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                            <i class="fa-solid fa-plus-circle text-rose-500"></i>
                            <span>新增一次产检与B超记录：</span>
                        </div>
                        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">检查日期 *</label>
                                <input type="date" id="fetalDate" ref="fetalDate" required @click="onDatePickerClick($event)" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg cursor-pointer">
                            </div>
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">孕周 (如: 18周+4天) *</label>
                                <input type="text" id="fetalWeek" ref="fetalWeek" placeholder="如: 18周+4天" required class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">胎心率 (bpm)</label>
                                <input type="number" id="fetalHeartRate" ref="fetalHeartRate" placeholder="如: 148" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">胎儿估重 (g)</label>
                                <input type="number" id="fetalWeight" ref="fetalWeight" placeholder="如: 230" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                        </div>

                        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">双顶径 BPD (mm)</label>
                                <input type="number" step="0.1" id="fetalBpd" ref="fetalBpd" placeholder="如: 42.5" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">股骨长 FL (mm)</label>
                                <input type="number" step="0.1" id="fetalFl" ref="fetalFl" placeholder="如: 27.2" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">腹围 AC (mm)</label>
                                <input type="number" step="0.1" id="fetalAc" ref="fetalAc" placeholder="如: 132.0" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">羊水最大深度 (mm)</label>
                                <input type="number" step="0.1" id="fetalAmniotic" ref="fetalAmniotic" placeholder="如: 45" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                        </div>

                        <div>
                            <label class="block text-slate-600 mb-1 font-medium txtleft">医生总结与护理提示 *</label>
                            <textarea id="fetalDoctorSummary" ref="fetalDoctorSummary" rows="2" placeholder="填写大排畸/NT筛查结论、医生嘱咐或奶爸感悟..." required class="w-full px-3 py-1.5 border border-slate-300 rounded-lg"></textarea>
                        </div>

                        <div class="flex justify-end">
                            <button type="submit" class="bg-rose-600 hover:bg-rose-700 text-white px-5 py-2 rounded-lg font-bold transition shadow-xs">保存本次产检记录</button>
                        </div>
                    </form>

                    <!-- History Fetal Records Container -->
                    <div class="space-y-3">
                        <h4 class="font-bold text-xs text-slate-800 txtleft">📋 历史产检与B超测量档案库：</h4>
                        <div id="fetalHistoryContainer" ref="fetalHistoryContainer" @click="delegateClick" class="space-y-3">
                            <!-- Dynamic rendered fetal checkup items -->
                        </div>
                    </div>
                </div>
            </section>

            <!-- ================= TAB 3: 时光成长日记 (年月归档) ================= -->
            <section id="tab-diary" ref="tab-diary" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-5">

                    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-100 pb-3 gap-2">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-book-bookmark text-indigo-500"></i>
                                <span>时光成长日记 (按年月分组归档)</span>
                            </h3>
                            <p class="text-xs text-slate-500">自动按年月聚合的微型日记，一排展示 4-5 篇，无大图干扰。点击可弹窗查看完整图文、视频与语音。</p>
                        </div>

                        <button @click="openModalGuarded('quickAddModal')" class="bg-sky-600 hover:bg-sky-700 text-white text-xs px-3.5 py-2 rounded-lg font-semibold shadow-xs transition flex items-center space-x-1.5">
                            <i class="fa-solid fa-pen-to-square"></i>
                            <span>写新日记</span>
                        </button>
                    </div>

                    <!-- Category Filter Buttons -->
                    <div class="flex flex-wrap gap-2 text-xs">
                        <button @click="filterDiary('ALL')" :class="['diary-filter-btn px-3 py-1 rounded-lg font-medium transition', currentDiaryCategory === 'ALL' ? 'active bg-sky-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-sky-50']">全部日记 (<span id="diaryCountAll" ref="diaryCountAll">0</span>)</button>
                        <button v-for="item in diaryList" :key="item.id || item.dictName" @click="filterDiary(item.id)" :class="['diary-filter-btn px-3 py-1 rounded-lg font-medium transition', currentDiaryCategory === item.id ? 'active bg-sky-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-sky-50']">{{ item.dictName }} ({{ diaryCountByType(item.id) }})</button>
                    </div>

                    <!-- Grouped Diaries Container (By Year-Month) -->
                    <div id="groupedDiariesContainer" ref="groupedDiariesContainer" @click="delegateClick" class="space-y-6">
                        <!-- Dynamic rendering for grouped compact card diaries -->
                    </div>
                </div>
            </section>

            <!-- ================= TAB 3.5: 秉宣时光成长相册 (多媒体素材库) ================= -->
            <section id="tab-album" ref="tab-album" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-5">

                    <!-- Header & Stats -->
                    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-100 pb-4 gap-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-images text-teal-600"></i>
                                <span>{{this.userInfo.chlidName}}时光成长相册影像馆</span>
                            </h3>
                            <p class="text-xs text-slate-500 mt-0.5">记录{{this.userInfo.chlidName}}每一个动人瞬间，支持照片与视频分类筛选、时间轴归档及全屏沉浸播放。</p>
                        </div>

                        <!-- Stats & Upload Launcher Button -->
                        <div class="flex items-center space-x-2">
                            <div class="bg-teal-50 border border-teal-200/80 px-3 py-1.5 rounded-lg text-xs font-mono text-teal-800 flex items-center space-x-3">
                                <span>📷 照片: <strong id="albumPhotoCount" ref="albumPhotoCount">0</strong></span>
                                <span>🎥 视频: <strong id="albumVideoCount" ref="albumVideoCount">0</strong></span>
                            </div>
                            <button @click="openModalGuarded('addAlbumMediaModal')" class="bg-teal-600 hover:bg-teal-700 text-white text-xs px-3.5 py-2 rounded-lg font-semibold shadow-xs transition flex items-center space-x-1.5">
                                <i class="fa-solid fa-cloud-arrow-up"></i>
                                <span>上传素材到相册</span>
                            </button>
                        </div>
                    </div>

                    <!-- Filter Controls (Tags & Types) -->
                    <div class="space-y-3 bg-slate-50 p-3.5 rounded-xl border border-slate-200/80 text-xs">

                        <!-- Filter Row 1: Media Types -->
                        <div class="flex items-center space-x-2">
                            <span class="font-bold text-slate-700 flex items-center gap-1 min-w-[70px]">
                                <i class="fa-solid fa-filter text-teal-500"></i> 素材类型:
                            </span>
                            <div class="flex space-x-1.5">
                                <button @click="filterAlbumType('ALL')" id="album-type-ALL" :class="['album-type-btn px-3 py-1 rounded-lg font-medium transition', currentAlbumType === 'ALL' ? 'bg-teal-600 text-white' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100']">全部素材</button>
                                <button @click="filterAlbumType('image')" id="album-type-image" :class="['album-type-btn px-3 py-1 rounded-lg font-medium transition', currentAlbumType === 'image' ? 'bg-teal-600 text-white' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100']">📷 照片画廊</button>
                                <button @click="filterAlbumType('video')" id="album-type-video" :class="['album-type-btn px-3 py-1 rounded-lg font-medium transition', currentAlbumType === 'video' ? 'bg-teal-600 text-white' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100']">🎥 视频回忆</button>
                            </div>
                        </div>

                        <!-- Filter Row 2: Theme Tags -->
                        <div class="flex flex-wrap items-center gap-1.5 pt-1 border-t border-slate-200/60">
                            <span class="font-bold text-slate-700 flex items-center gap-1 min-w-[70px]">
                                <i class="fa-solid fa-tags text-teal-500"></i> 主题分类:
                            </span>
                            <button @click="filterAlbumTag('ALL')" :class="['album-tag-btn px-2.5 py-0.5 rounded-md text-[11px] font-medium transition', currentAlbumTag === 'ALL' ? 'active bg-slate-800 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-teal-50']">全部标签</button>
                            <button v-for="item in albumList" :key="item.id" @click="filterAlbumTag(item.id)" :class="['album-tag-btn px-2.5 py-0.5 rounded-md text-[11px] font-medium transition', currentAlbumTag == item.id ? 'active bg-slate-800 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-teal-50']">{{ item.dictName }}</button>
                        </div>
                    </div>

                    <!-- Album Gallery Grid (Grouped by Year-Month) -->
                    <div id="albumGalleryContainer" ref="albumGalleryContainer" @click="delegateClick" class="space-y-6">
                        <!-- Dynamic rendered media cards -->
                    </div>
                </div>
            </section>

            <!-- ================= TAB 4: 0-3岁科学喂养&4个月辅食计划 ================= -->
            <section id="tab-guide" ref="tab-guide" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-6">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800">0-3岁科学喂养&4个月辅食计划</h3>
                            <p class="text-xs text-slate-500">涵盖 0-1 岁液体喂养、6-10个月辅食菜谱、1-2 岁幼儿膳食过渡及 2-3 岁家庭共餐全景方案。</p>
                        </div>
                        <span class="bg-emerald-100 text-emerald-800 text-xs px-2.5 py-1 rounded-full font-semibold">权威护理与辅食</span>
                    </div>

                    <!-- Feeding Roadmap Table for 0-3 Years -->
                    <div class="space-y-3">
                        <h4 class="font-bold text-xs text-slate-800 flex items-center gap-1.5">
                            <span>🍼</span>
                            <span>0-3 岁全阶段喂养结构、奶量与膳食安排全景表</span>
                        </h4>
                        <div class="overflow-x-auto border border-slate-200 rounded-lg">
                            <table class="w-full text-xs text-left">
                                <thead class="bg-slate-50 border-b border-slate-200 text-slate-700 font-semibold">
                                    <tr>
                                        <th class="p-2.5">年龄阶段</th>
                                        <th class="p-2.5">核心食物结构</th>
                                        <th class="p-2.5">每日奶量安排</th>
                                        <th class="p-2.5">餐次与食物形态</th>
                                        <th class="p-2.5">中美欧指南核心禁忌与注意</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-slate-100 text-slate-600">
                                    <tr>
                                        <td class="p-2.5 font-bold text-slate-800">0 - 6 月</td>
                                        <td class="p-2.5">纯母乳 / 配方奶</td>
                                        <td class="p-2.5">600 - 1000 ml (按需)</td>
                                        <td class="p-2.5">每日 6-12 次纯液体喂养</td>
                                        <td class="p-2.5">出生补充维D3 (400IU/天)，严禁喂水、蜂蜜或果汁。</td>
                                    </tr>
                                    <tr>
                                        <td class="p-2.5 font-bold text-slate-800">6 - 12 月</td>
                                        <td class="p-2.5">母乳/配方奶 + 高铁辅食</td>
                                        <td class="p-2.5">600 - 800 ml</td>
                                        <td class="p-2.5">3-4次奶 + 2-3次泥糊至小颗粒辅食</td>
                                        <td class="p-2.5">辅食优先补铁，绝对无盐无糖，1岁内禁止鲜牛奶。</td>
                                    </tr>
                                    <tr class="bg-emerald-50/40">
                                        <td class="p-2.5 font-bold text-emerald-900">1 - 2 岁</td>
                                        <td class="p-2.5">家庭固体食物为主，奶为辅</td>
                                        <td class="p-2.5">400 - 500 ml (可引入全脂鲜牛奶)</td>
                                        <td class="p-2.5">一日三餐家庭共餐 + 2次健康点心</td>
                                        <td class="p-2.5">食物剪小块防止窒息，微盐（低盐），不喝含糖饮料。</td>
                                    </tr>
                                    <tr class="bg-emerald-50/40">
                                        <td class="p-2.5 font-bold text-emerald-900">2 - 3 岁</td>
                                        <td class="p-2.5">完全融入家庭多样化均衡膳食</td>
                                        <td class="p-2.5">300 - 400 ml</td>
                                        <td class="p-2.5">一日三餐 + 1-2次点心 (自主进食)</td>
                                        <td class="p-2.5">培养独立用匙用叉习惯，避免整颗花生、坚果等窒息风险。</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- 4 Months Comprehensive Solid Food Plan & Recipes -->
                    <div class="bg-amber-50/50 border border-amber-200 rounded-xl p-3.5 sm:p-5 space-y-4 w-full">
                        <div class="flex justify-between items-center">
                            <div>
                                <h4 class="text-xs sm:text-sm font-bold text-amber-900 flex items-center space-x-2">
                                    <i class="fa-solid fa-utensils text-amber-600"></i>
                                    <span>6个月+ 跨度 4 个月的科学辅食菜谱打卡表 (按每3天一餐计划进行过敏排除)</span>
                                </h4>
                                <p class="text-[11px] sm:text-xs text-amber-800 mt-1">原则：优先强化铁米粉，每3天为一个食谱观察期，连续观察3天无过敏（无皮疹、腹泻、呕吐）再解锁下一种新食材。</p>
                            </div>
                        </div>

                        <!-- 4 Month Interactive Tabs -->
                        <div class="flex flex-wrap gap-1.5 sm:gap-2 border-b border-amber-200 pb-2 text-xs w-full">
                            <button @click="switchFoodMonth(1)" id="food-tab-1" ref="food-tab-1" class="food-month-btn bg-amber-600 text-white px-2.5 sm:px-3 py-1.5 rounded-lg font-bold transition text-[11px] sm:text-xs">第1个月：单一泥糊 (1-30天)</button>
                            <button @click="switchFoodMonth(2)" id="food-tab-2" ref="food-tab-2" class="food-month-btn bg-white text-amber-900 border border-amber-200 hover:bg-amber-100 px-2.5 sm:px-3 py-1.5 rounded-lg font-medium transition text-[11px] sm:text-xs">第2个月：混合肉菜泥 (31-60天)</button>
                            <button @click="switchFoodMonth(3)" id="food-tab-3" ref="food-tab-3" class="food-month-btn bg-white text-amber-900 border border-amber-200 hover:bg-amber-100 px-2.5 sm:px-3 py-1.5 rounded-lg font-medium transition text-[11px] sm:text-xs">第3个月：颗粒与手指食物 (61-90天)</button>
                            <button @click="switchFoodMonth(4)" id="food-tab-4" ref="food-tab-4" class="food-month-btn bg-white text-amber-900 border border-amber-200 hover:bg-amber-100 px-2.5 sm:px-3 py-1.5 rounded-lg font-medium transition text-[11px] sm:text-xs">第4个月：多样碎小块食 (91-120天)</button>
                        </div>

                        <!-- Month Food Content Area -->
                        <div id="foodMonthContent" ref="foodMonthContent" @click="delegateClick" class="space-y-3 text-xs w-full">
                            <!-- Dynamic food recipes rendering per 3-day block -->
                        </div>
                    </div>
                </div>
            </section>

            <!-- ================= TAB 5: 回避型奶爸的安全型依恋重塑与打卡 ================= -->
            <section id="tab-attachment" ref="tab-attachment" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-6">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-heart text-rose-500"></i>
                                <span>回避型奶爸的“安全型依恋 (Secure Attachment)” 重塑指南与亲子互动</span>
                            </h3>
                            <p class="text-xs text-slate-500">帮奶爸突破回避型人格的理智防御，为{{this.userInfo.chlidName}}筑造一生受益的底层安全感基地。</p>
                        </div>
                        <span class="bg-rose-100 text-rose-800 text-xs px-2.5 py-1 rounded-full font-semibold">依恋心理学</span>
                    </div>

                    <!-- Father's Self Awareness Box -->
                    <div class="bg-gradient-to-r from-rose-50 via-pink-50 to-amber-50 border border-rose-200 p-6 rounded-xl space-y-4 text-xs">
                        <div class="font-bold text-rose-900 text-sm flex items-center gap-2 border-b border-rose-200/60 pt-1 pb-4 px-1">
                            <i class="fa-solid fa-user-shield text-rose-600"></i>
                            <span>💡 给回避型奶爸的心灵信笺：觉察即是破局的开始</span>
                        </div>
                        <div class="px-1.5">
                            <p class="text-slate-700 leading-relaxed">
                                回避型依恋（Avoidant Attachment）往往源于自身童年未被敏感回应的体验，容易习惯用“理智、独立、退缩、压抑情绪”作为心理防御。当面对婴儿剧烈的哭闹或高度情感需求时，回避型奶爸容易产生**“情绪淹没感”**，并下意识想要逃离或冷处理。
                            </p>
                            <p class="text-slate-700 leading-relaxed">
                                <strong class="text-rose-900">但请记住：</strong> 依恋类型并非基因决定，而是由后天**“可预测的敏感回应”**重塑的！只要奶爸在{{this.userInfo.chlidName}} 0-3 岁建立起 **“敏锐回应 + 安全基地 + 关系修补”** 机制，孩子就能成长为阳光、自信、勇敢爱人的**安全型依恋（Secure Attachment）**人格！
                            </p>
                        </div>
                    </div>

                    <!-- 4 Attachment Stages Guidance & Action Checklists -->
                    <div class="space-y-5">
                        <!-- Stage 1 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-rose-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-rose-600 text-white flex items-center justify-center text-xs">1</span>
                                    <span>0 - 6 个月：身体触觉、声音温度与“3秒敏锐回应”</span>
                                </h4>
                                <span class="bg-rose-100 text-rose-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">生理安全感基石</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 回避型奶爸面临的挑战：</strong>
                                    <p class="text-slate-600 leading-relaxed">对长时间亲密肢体接触或无原因哭闹感到焦虑，下意识想把哭闹归结为“孩子习惯不好”而推开孩子。</p>
                                    <strong class="text-slate-800">🛡️ 依恋重塑核心策略：</strong>
                                    <p class="text-slate-600 leading-relaxed">建立“哭闹=信号，而非攻击”的认知。婴儿6个月前无法被“惯坏”，每一次及时抱抚都在给大脑注入安全神经递质（催产素与多巴胺）。</p>
                                </div>
                                <div class="bg-white border border-rose-200 p-3 rounded-lg space-y-2 text-slate-700">
                                    <strong class="text-rose-900 flex items-center gap-1">
                                        <i class="fa-solid fa-list-check text-rose-600"></i>
                                        <span>每日必做依恋打卡工作：</span>
                                    </strong>
                                    <ul class="list-disc list-inside space-y-1 text-slate-600 text-[11px]">
                                        <li>每日至少完成 15 分钟专注身体接触/抚触（袋鼠式抱抚）。</li>
                                        <li>哭闹发生时，承诺在 3-5 秒内给予眼神注视与温和声音回应。</li>
                                        <li>喂奶时保持眼睛对视（Eye Contact）并给予微笑镜像。</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <!-- Stage 2 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-amber-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-amber-600 text-white flex items-center justify-center text-xs">2</span>
                                    <span>6 - 18 个月：安全基地（Safe Haven）与不溜走告别仪式</span>
                                </h4>
                                <span class="bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">分离焦虑应对</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 回避型奶爸面临的挑战：</strong>
                                    <p class="text-slate-600 leading-relaxed">宝宝出现分离焦虑粘人时，容易感到被捆绑，甚至想“趁宝宝不注意偷偷溜走”。</p>
                                    <strong class="text-slate-800">🛡️ 依恋重塑核心策略：</strong>
                                    <p class="text-slate-600 leading-relaxed">绝对不要偷溜！偷偷溜走会让宝宝杏仁核高度恐慌并诱发焦虑型/回避型人格。做“安全基地”：支持他探索，在他回头看你时给予确定鼓励。</p>
                                </div>
                                <div class="bg-white border border-amber-200 p-3 rounded-lg space-y-2 text-slate-700">
                                    <strong class="text-amber-900 flex items-center gap-1">
                                        <i class="fa-solid fa-list-check text-amber-600"></i>
                                        <span>每日必做依恋打卡工作：</span>
                                    </strong>
                                    <ul class="list-disc list-inside space-y-1 text-slate-600 text-[11px]">
                                        <li>每次离开前进行“温暖固定的告别仪式”（抱一下、对视说明归来时间）。</li>
                                        <li>做“躲猫猫（Peek-a-boo）”游戏，强化物体恒存与“爸爸会回来”的信念。</li>
                                        <li>孩子向外探索受吓归来时，立刻敞开双臂拥抱充能。</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <!-- Stage 3 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-indigo-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs">3</span>
                                    <span>18 个月 - 3 岁：情绪共情、拒绝冷暴力与禁闭</span>
                                </h4>
                                <span class="bg-indigo-100 text-indigo-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">情绪镜像与修补</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 回避型奶爸面临的挑战：</strong>
                                    <p class="text-slate-600 leading-relaxed">孩子发脾气撒泼时，回避型奶爸容易启动“冷漠隔离/讲冷冰冰的大道理”防御。</p>
                                    <strong class="text-slate-800">🛡️ 依恋重塑核心策略：</strong>
                                    <p class="text-slate-600 leading-relaxed">严禁冷暴力与无回应隔离！先处理情绪，再讲道理。如果奶爸一时情绪失控吼了孩子，一定要在冷静后主动进行“关系破裂修补（Rupture & Repair）”。</p>
                                </div>
                                <div class="bg-white border border-indigo-200 p-3 rounded-lg space-y-2 text-slate-700">
                                    <strong class="text-indigo-900 flex items-center gap-1">
                                        <i class="fa-solid fa-list-check text-indigo-600"></i>
                                        <span>每日必做依恋打卡工作：</span>
                                    </strong>
                                    <ul class="list-disc list-inside space-y-1 text-slate-600 text-[11px]">
                                        <li>每天至少 1 次帮{{this.userInfo.chlidName}}命名情绪（“你现在很沮丧对不对，爸爸陪着你”）。</li>
                                        <li>若自己产生情绪退缩，先深呼吸5秒，告诉孩子：“爸爸需要冷静一下，但我依然爱你”。</li>
                                        <li>固定睡前 10-15 分钟“无手机高质量陪伴”。</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <!-- Stage 4 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-purple-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs">4</span>
                                    <span>3 岁 - 6 岁：主动口头表达爱意与自信独立支持</span>
                                </h4>
                                <span class="bg-purple-100 text-purple-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">完全安全型形成</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 回避型奶爸面临的挑战：</strong>
                                    <p class="text-slate-600 leading-relaxed">说出“爸爸爱你/我想你”等感性话语时感到肉麻或别扭，习惯只讨论“学习与任务”。</p>
                                    <strong class="text-slate-800">🛡️ 依恋重塑核心策略：</strong>
                                    <p class="text-slate-600 leading-relaxed">勇敢打破言语羞耻感！安全型依恋的孩子需要明确听到爱与认可，明白“即使我做错了事，爸爸对我的爱依然不会减少”。</p>
                                </div>
                                <div class="bg-white border border-purple-200 p-3 rounded-lg space-y-2 text-slate-700">
                                    <strong class="text-purple-900 flex items-center gap-1">
                                        <i class="fa-solid fa-list-check text-purple-600"></i>
                                        <span>每日必做依恋打卡工作：</span>
                                    </strong>
                                    <ul class="list-disc list-inside space-y-1 text-slate-600 text-[11px]">
                                        <li>每日至少口头表达 1 次：“{{this.userInfo.chlidName}}，爸爸非常爱你/今天为你感到骄傲”。</li>
                                        <li>平视蹲下，认真听孩子分享的一件小事，不中途打断。</li>
                                        <li>鼓励自主探索并提供坚定后盾支撑。</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <!-- ================= TAB 6: 0-3岁大运动与神经肌肉发育 ================= -->
            <section id="tab-motor" ref="tab-motor" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-6">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-person-running text-cyan-600"></i>
                                <span>0-3岁大运动发育、肌肉骨骼与神经生理演进机制</span>
                            </h3>
                            <p class="text-xs text-slate-500">解密婴儿“头尾法则（Cephalocaudal）”与“神经髓鞘化”原理：为什么在这个阶段能做出这些动作？</p>
                        </div>
                        <span class="bg-cyan-100 text-cyan-800 text-xs px-2.5 py-1 rounded-full font-semibold">运动解剖学</span>
                    </div>

                    <!-- Core Scientific Concept Box -->
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                        <div class="bg-cyan-50/70 border border-cyan-200 p-3.5 rounded-xl space-y-1.5">
                            <h4 class="font-bold text-cyan-900 flex items-center gap-1">
                                <i class="fa-solid fa-arrows-up-down"></i>
                                <span>1. 头尾发展规律 (Cephalocaudal)</span>
                            </h4>
                            <p class="text-slate-600 leading-relaxed">神经与肌肉控制遵循由上至下（头部 ➜ 颈部 ➜ 躯干 ➜ 下肢）发展。宝宝先学会抬头控制眼睛与头部，再学会控制双臂坐立，最后控制双腿行走。</p>
                        </div>
                        <div class="bg-blue-50/70 border border-blue-200 p-3.5 rounded-xl space-y-1.5">
                            <h4 class="font-bold text-blue-900 flex items-center gap-1">
                                <i class="fa-solid fa-bolt"></i>
                                <span>2. 神经髓鞘化 (Myelination)</span>
                            </h4>
                            <p class="text-slate-600 leading-relaxed">神经轴突被脂肪性髓鞘包裹，使电信号传输速度提升数十倍。皮质脊髓束（Corticospinal tract）的髓鞘化进度直接决定了精细运动与协调性。</p>
                        </div>
                        <div class="bg-indigo-50/70 border border-indigo-200 p-3.5 rounded-xl space-y-1.5">
                            <h4 class="font-bold text-indigo-900 flex items-center gap-1">
                                <i class="fa-solid fa-shield-halved"></i>
                                <span>3. 原始反射消退 (Reflex Integration)</span>
                            </h4>
                            <p class="text-slate-600 leading-relaxed">摩罗反射（Moro）、非对称强直性颈反射（ATNR）等由脑干控制的原始反射必须逐步整合消退，大脑皮层才能取得对肌肉的主动自主控制。</p>
                        </div>
                    </div>

                    <!-- Timeline Stage Analysis -->
                    <div class="space-y-4 pt-2">
                        <!-- Stage 1 -->
                        <div class="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <span class="font-extrabold text-sm text-cyan-800">📌 0 - 3 个月：抬头与颈部稳定</span>
                                <span class="bg-cyan-200 text-cyan-900 text-[10px] px-2 py-0.5 rounded font-mono">关键动作：趴卧抬头90度</span>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div>
                                    <strong class="text-slate-800">💪 解剖与肌肉参与：</strong>
                                    <p class="text-slate-600 mt-1">胸锁乳突肌与头夹肌锻炼发力。颈椎第一弯曲（颈曲）在此阶段趴卧练习中开始建立，为支撑头部重量打下基础。</p>
                                </div>
                                <div>
                                    <strong class="text-slate-800">🧠 神经系统突破：</strong>
                                    <p class="text-slate-600 mt-1">前庭系统接收重力感受，前庭脊髓束开始建立连接。摩罗惊跳反射在3个月末逐渐减弱。</p>
                                </div>
                            </div>
                        </div>

                        <!-- Stage 2 -->
                        <div class="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <span class="font-extrabold text-sm text-blue-800">📌 4 - 7 个月：自主翻身与独坐</span>
                                <span class="bg-blue-200 text-blue-900 text-[10px] px-2 py-0.5 rounded font-mono">关键动作：仰卧翻身、三点支撑坐</span>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div>
                                    <strong class="text-slate-800">💪 解剖与肌肉参与：</strong>
                                    <p class="text-slate-600 mt-1">腹直肌与腹斜肌侧向扭转力增强。坐立时腰椎弯曲（腰曲）建立，竖脊肌与核心肌群支撑脊柱，髋关节外展肌锁定盘腿坐姿。</p>
                                </div>
                                <div>
                                    <strong class="text-slate-800">🧠 神经系统突破：</strong>
                                    <p class="text-slate-600 mt-1">ATNR（击剑反射）消退，双手越过身体中线的控制能力建立。小脑参与维持身体重心平衡与空间定向。</p>
                                </div>
                            </div>
                        </div>

                        <!-- Stage 3 -->
                        <div class="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <span class="font-extrabold text-sm text-indigo-800">📌 8 - 12 个月：四肢爬行与扶物站立</span>
                                <span class="bg-indigo-200 text-indigo-900 text-[10px] px-2 py-0.5 rounded font-mono">关键动作：手膝爬行、扶家具拉站</span>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div>
                                    <strong class="text-slate-800">💪 解剖与肌肉参与：</strong>
                                    <p class="text-slate-600 mt-1">股四头肌、臀大肌与小腿腓肠肌开始接受重力负荷。手掌骨与腕骨承受体重，为精细抓握提供本体感觉基石。</p>
                                </div>
                                <div>
                                    <strong class="text-slate-800">🧠 神经系统突破：</strong>
                                    <p class="text-slate-600 mt-1">大脑两侧半球通过胼胝体进行高频对侧对等信息交互（交叉手脚爬行），大大促进双侧大脑发育。</p>
                                </div>
                            </div>
                        </div>

                        <!-- Stage 4 -->
                        <div class="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <span class="font-extrabold text-sm text-purple-800">📌 12 - 36 个月：独立行走、跑步与跳跃</span>
                                <span class="bg-purple-200 text-purple-900 text-[10px] px-2 py-0.5 rounded font-mono">关键动作：稳步行走、双脚离地跳</span>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div>
                                    <strong class="text-slate-800">💪 解剖与肌肉参与：</strong>
                                    <p class="text-slate-600 mt-1">足弓逐步发育形成缓冲震荡。下肢骨骼（股骨与胫骨）钙化坚硬，踝关节韧带稳定性成熟，支撑跑跳时的爆发性冲击。</p>
                                </div>
                                <div>
                                    <strong class="text-slate-800">🧠 神经系统突破：</strong>
                                    <p class="text-slate-600 mt-1">运动皮层完成完全意志性控制，步态动态调整自动化（不需要大脑全程集中注意力），感觉统合高度成熟。</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ================= TAB 7: 0-3岁大脑脑科学 ================= -->
            <section id="tab-brain" ref="tab-brain" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-6">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-brain text-pink-500"></i>
                                <span>0-3岁大脑脑科学：神经结构、压力与快乐激素调控图谱</span>
                            </h3>
                            <p class="text-xs text-slate-500">解密皮质醇（压力）、催产素（依恋）、内啡肽/多巴胺（快乐与探索）的分泌机制，指导奶爸科学下调压力激素，激发快乐与安全依恋。</p>
                        </div>
                        <span class="bg-pink-100 text-pink-800 text-xs px-2.5 py-1 rounded-full font-semibold">脑科学与神经递质</span>
                    </div>

                    <!-- 3 Major Brain Structures Interactive Cards -->
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
                        
                        <!-- Structure 1: 海马体 -->
                        <div class="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                                    <span class="text-xl">🌊</span>
                                    <span>海马体 (Hippocampus)</span>
                                </h4>
                                <span class="bg-indigo-100 text-indigo-700 text-[10px] px-2 py-0.5 rounded font-bold">记忆转换中心</span>
                            </div>
                            <div class="text-xs text-slate-600 space-y-2 leading-relaxed">
                                <p><strong>核心功能：</strong> 负责将短时记忆转化为长时记忆，处理空间定向与事件情境。</p>
                                <p><strong>0-3岁发育机制：</strong> 0-3岁时海马体齿状回正经历爆发性的神经元新生。因为新神经元不断重构神经网络，旧的突触连接被替换，导致了心理学上的**“婴儿期失忆症（Infantile Amnesia）”**（人很难记住3岁以前的具体记忆）。</p>
                                <div class="bg-indigo-50 border border-indigo-100 p-2.5 rounded-lg text-indigo-900 font-medium">
                                    💡 <strong>奶爸指导：</strong> 虽然孩子长大后记不起3岁前的具体事件，但海马体与边缘系统形成的**“内隐情绪记忆（Implicit Memory）”**会陪伴终生！爸爸给的安全感与温暖拥抱，会永久写入底层的安全感基因。
                                </div>
                            </div>
                        </div>

                        <!-- Structure 2: 杏仁核 -->
                        <div class="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                                    <span class="text-xl">🚨</span>
                                    <span>杏仁核 (Amygdala)</span>
                                </h4>
                                <span class="bg-rose-100 text-rose-700 text-[10px] px-2 py-0.5 rounded font-bold">情绪与威胁警报器</span>
                            </div>
                            <div class="text-xs text-slate-600 space-y-2 leading-relaxed">
                                <p><strong>核心功能：</strong> 识别恐惧、危险、威胁，触发“战或逃”生理应激反应。</p>
                                <p><strong>0-3岁发育机制：</strong> 杏仁核在出生时就已经相对成熟，能快速感知爸爸的语气、面部表情与紧张气氛。8-18个月出现的**分离焦虑（Separation Anxiety）**就是因为杏仁核发现熟悉照顾者离开时触发了威胁警报。</p>
                                <div class="bg-rose-50 border border-rose-100 p-2.5 rounded-lg text-rose-900 font-medium">
                                    💡 <strong>奶爸指导：</strong> 宝宝哭闹时，杏仁核正处于超负荷警报状态。此时严厉训斥只会让杏仁核更加恐慌。奶爸需要用轻柔低沉的语调与温暖拥抱，帮助其下调杏仁核兴奋度（Co-regulation 共同调节）。
                                </div>
                            </div>
                        </div>

                        <!-- Structure 3: 前额叶皮层 -->
                        <div class="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                                    <span class="text-xl">🧭</span>
                                    <span>前额叶皮层 (PFC)</span>
                                </h4>
                                <span class="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded font-bold">理性控制与指挥官</span>
                            </div>
                            <div class="text-xs text-slate-600 space-y-2 leading-relaxed">
                                <p><strong>核心功能：</strong> 理性逻辑、冲动抑制、延迟满足、工作记忆与自我情绪调节。</p>
                                <p><strong>0-3岁发育机制：</strong> 前额叶皮层是大脑中**发育最晚、耗时最长**的区域（要到25岁左右才完全发育成熟！）。2岁左右孩子大发脾气、撒泼打滚，根本原因是杏仁核爆发时，极其幼稚的前额叶皮层根本无法提供足够的“抑制性神经递质（GABA）”来压制冲动！</p>
                                <div class="bg-emerald-50 border border-emerald-100 p-2.5 rounded-lg text-emerald-900 font-medium">
                                    💡 <strong>奶爸指导：</strong> 不要指望2岁孩子能“像大人一样懂理智”。每次冷静地帮孩子命名情绪（“我知道你因为玩具坏了很生气”），就是在帮他的前额叶皮层建立神经连接通路！
                                </div>
                            </div>
                        </div>

                    </div>

                    <!-- Neurohormones Deep Dive Section -->
                    <div class="border-t border-slate-200 pt-5 space-y-4">
                        <div class="flex items-center space-x-2">
                            <span class="p-2 bg-pink-100 text-pink-700 rounded-xl text-base"><i class="fa-solid fa-flask"></i></span>
                            <div>
                                <h4 class="font-extrabold text-slate-800 text-sm">0-3岁四大核心育儿神经激素与化学递质</h4>
                                <p class="text-[11px] text-slate-500">婴儿大脑通过激素调控情绪与生理发育。奶爸的行为是孩子内分泌系统的最高“调节阀”。</p>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                            
                            <!-- Hormone 1: 皮质醇 -->
                            <div class="bg-gradient-to-br from-rose-50 to-orange-50/40 border border-rose-200 rounded-xl p-4 space-y-2.5">
                                <div class="flex justify-between items-center border-b border-rose-200/80 pb-2">
                                    <span class="font-extrabold text-rose-900 flex items-center gap-1.5 text-xs sm:text-sm">
                                        <i class="fa-solid fa-triangle-exclamation text-rose-600"></i>
                                        <span>1. 皮质醇 (Cortisol) —— 压力与应激激素</span>
                                    </span>
                                    <span class="bg-rose-200 text-rose-900 text-[10px] px-2 py-0.5 rounded font-mono font-bold">需严格管控下调</span>
                                </div>
                                <div class="space-y-1.5 text-slate-700 leading-relaxed">
                                    <p><strong>⚡ 何时飙升分泌：</strong> 长时间哭闹无回应、缺乏安全感、惊吓受暴击、过度刺激、父子冷漠隔离或家庭吵闹应激。</p>
                                    <p><strong>⚠️ 高皮质醇毒性危害：</strong> 持续高水平皮质醇会透过血脑屏障，直接损害海马体突触连接，导致杏仁核过度警觉（易惊恐、敏感），并阻碍前额叶皮层正常发育。</p>
                                    <div class="bg-white/80 p-2.5 rounded-lg border border-rose-200 text-rose-950 font-medium">
                                        🛡️ <strong>奶爸抑制皮质醇方案：</strong> 哭闹时进行“3-5秒敏锐回应”；进行胸对胸皮肤接触（Kangaroo Care）；用沉稳低频的男低音安抚；提供预测性强的规律作息。
                                    </div>
                                </div>
                            </div>

                            <!-- Hormone 2: 催产素 -->
                            <div class="bg-gradient-to-br from-pink-50 to-rose-50/30 border border-pink-200 rounded-xl p-4 space-y-2.5">
                                <div class="flex justify-between items-center border-b border-pink-200/80 pb-2">
                                    <span class="font-extrabold text-pink-900 flex items-center gap-1.5 text-xs sm:text-sm">
                                        <i class="fa-solid fa-heart text-pink-600"></i>
                                        <span>2. 催产素 (Oxytocin) —— 爱的连接与依恋激素</span>
                                    </span>
                                    <span class="bg-pink-200 text-pink-900 text-[10px] px-2 py-0.5 rounded font-mono font-bold">安全依恋基石</span>
                                </div>
                                <div class="space-y-1.5 text-slate-700 leading-relaxed">
                                    <p><strong>💖 何时爆发分泌：</strong> 温和的眼睛注视对视、皮肤接触、轻柔抚触按摩、拥抱贴贴、听到爸爸温暖哼唱时。</p>
                                    <p><strong>✨ 神经生理作用：</strong> 催产素是皮质醇的天然“解毒剂”！它能迅速关闭杏仁核警报，降低心率与血压，诱发深层信任感与安全感，奠定安全型依恋基础。</p>
                                    <div class="bg-white/80 p-2.5 rounded-lg border border-pink-200 text-pink-950 font-medium">
                                        🚀 <strong>奶爸提升催产素方案：</strong> 每天至少15分钟无手机专注抚触；喂奶时维持微笑对视；孩子受惊时敞开双臂拥抱充能。
                                    </div>
                                </div>
                            </div>

                            <!-- Hormone 3: 内啡肽 -->
                            <div class="bg-gradient-to-br from-amber-50 to-yellow-50/40 border border-amber-200 rounded-xl p-4 space-y-2.5">
                                <div class="flex justify-between items-center border-b border-amber-200/80 pb-2">
                                    <span class="font-extrabold text-amber-900 flex items-center gap-1.5 text-xs sm:text-sm">
                                        <i class="fa-solid fa-smile-beam text-amber-600"></i>
                                        <span>3. 内啡肽 (Endorphin) —— 天然镇痛与满足愉快激素</span>
                                    </span>
                                    <span class="bg-amber-200 text-amber-900 text-[10px] px-2 py-0.5 rounded font-mono font-bold">生理舒适与欢笑</span>
                                </div>
                                <div class="space-y-1.5 text-slate-700 leading-relaxed">
                                    <p><strong>🌿 何时自然释放：</strong> 有节奏的摇晃、背部轻拍、舒缓的音乐、出牙或肠绞痛缓解后、欢快打闹与咯咯大笑时。</p>
                                    <p><strong>✨ 神经生理作用：</strong> 提供天然镇痛与止痛作用，带来深层身心放松感与愉悦感，帮助婴儿平稳入睡与缓解身体不适。</p>
                                    <div class="bg-white/80 p-2.5 rounded-lg border border-amber-200 text-amber-950 font-medium">
                                        🚀 <strong>奶爸提升内啡肽方案：</strong> 做节奏性轻拍与摇篮曲呼唤；出牙期提供冰镇咬咬乐；多玩“躲猫猫（Peek-a-boo）”引发咯咯笑。
                                    </div>
                                </div>
                            </div>

                            <!-- Hormone 4: 多巴胺与血清素 -->
                            <div class="bg-gradient-to-br from-indigo-50 to-sky-50/40 border border-indigo-200 rounded-xl p-4 space-y-2.5">
                                <div class="flex justify-between items-center border-b border-indigo-200/80 pb-2">
                                    <span class="font-extrabold text-indigo-900 flex items-center gap-1.5 text-xs sm:text-sm">
                                        <i class="fa-solid fa-sun text-indigo-600"></i>
                                        <span>4. 多巴胺&血清素 (Dopamine & Serotonin) —— 探索与情绪稳定</span>
                                    </span>
                                    <span class="bg-indigo-200 text-indigo-900 text-[10px] px-2 py-0.5 rounded font-mono font-bold">探索动力与专注</span>
                                </div>
                                <div class="space-y-1.5 text-slate-700 leading-relaxed">
                                    <p><strong>🌟 何时激发释放：</strong> 好奇心被激活（成功抓握新玩具）、大运动尝试成功被赞许时（多巴胺）；适度户外阳光照射、高质感睡眠与均衡营养（血清素）。</p>
                                    <p><strong>✨ 神经生理作用：</strong> 多巴胺驱动自主学习与探索欲望；血清素维持整体情绪平稳与昼夜作息规律。</p>
                                    <div class="bg-white/80 p-2.5 rounded-lg border border-indigo-200 text-indigo-950 font-medium">
                                        🚀 <strong>奶爸提升方案：</strong> 每日带孩子户外晒太阳30分钟；对宝宝微小的运动突破（如第一次翻身成功）给予热情肯定与掌声。
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>

                </div>
            </section>

            <!-- ================= TAB 8: 0-10岁儿童情绪理解演进 ================= -->
            <section id="tab-emotion" ref="tab-emotion" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-6">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-heart-pulse text-rose-500"></i>
                                <span>0 - 10 岁儿童情绪认知、理解力与心理演进全景路线图</span>
                            </h3>
                            <p class="text-xs text-slate-500">根据发展心理学 Pons & Harris 情绪理解三阶模型：揭密孩子为何要到 10 岁左右才能彻底理解所有复杂情绪。</p>
                        </div>
                        <span class="bg-rose-100 text-rose-800 text-xs px-2.5 py-1 rounded-full font-semibold">发展心理学</span>
                    </div>

                    <!-- Key Psychological Insight Banner -->
                    <div class="bg-gradient-to-r from-rose-50 via-amber-50 to-sky-50 border border-rose-200 p-4 rounded-xl space-y-2 text-xs">
                        <div class="font-bold text-rose-900 flex items-center space-x-2 text-sm">
                            <i class="fa-solid fa-lightbulb text-amber-500"></i>
                            <span>💡 奶爸必读：为什么心理学研究表明“孩子要到 10 岁左右才能彻底理解所有类型的情绪”？</span>
                        </div>
                        <p class="text-slate-700 leading-relaxed">
                            情绪理解并非一蹴而就！幼儿期（2-4岁）只能理解**基本外在情绪（喜怒哀惧）**；5-7岁开始理解**心理信念与外在表情掩饰（如失望时佯装微笑）**；而只有到了 **9-10 岁（小学高年级）**，大脑的心智理论（Theory of Mind）与前额叶网络高度整合后，孩子才能真正掌握**矛盾/混合情绪（Ambivalent Emotions，如同时感到又高兴又害怕）**、**元情绪（Meta-emotions）**以及**道德内疚与自我反思**。
                        </p>
                    </div>

                    <!-- 4 Developmental Stages of Emotion Understanding -->
                    <div class="space-y-6">
                        
                        <!-- Phase 1 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-sky-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-sky-600 text-white flex items-center justify-center text-xs">1</span>
                                    <span>阶段一：0 - 2 岁 (情绪直觉、依恋与自我意识萌芽)</span>
                                </h4>
                                <span class="bg-sky-100 text-sky-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">基本情绪识别</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 情绪理解发展特征：</strong>
                                    <ul class="list-disc list-inside text-slate-600 space-y-1 leading-relaxed">
                                        <li><strong>0-6个月：</strong> 依赖“情绪传染”，通过声音与面部辨别喜怒哀惧。</li>
                                        <li><strong>8-12个月：</strong> 出现“社会性参照”，遇到陌生环境时观察奶爸的面部表情来判断安全与否。</li>
                                        <li><strong>18-24个月：</strong> “镜像自我认知”完成，首次萌发**自我意识情绪（羞耻 Shame、骄傲 Pride）**。</li>
                                    </ul>
                                </div>
                                <div class="bg-sky-50 border border-sky-100 p-3 rounded-lg space-y-1 text-sky-900">
                                    <strong>👨‍👦 奶爸育儿应对策略：</strong>
                                    <p class="leading-relaxed">提供稳定的安全依恋。面对宝宝哭闹，及时给予回应与抚触，让宝宝的大脑建立“外界是安全可预测”的底层信念。</p>
                                </div>
                            </div>
                        </div>

                        <!-- Phase 2 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-emerald-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs">2</span>
                                    <span>阶段二：2 - 5 岁 (外在因果理解与初步共情)</span>
                                </h4>
                                <span class="bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">外在因果与标签</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 情绪理解发展特征：</strong>
                                    <ul class="list-disc list-inside text-slate-600 space-y-1 leading-relaxed">
                                        <li>能明确建立“外在事件 ➜ 情绪”的因果链条（如：“玩具抢走了，所以我生气的”）。</li>
                                        <li>掌握情绪词汇（高兴、难过、害怕、生气），能够为自己和同伴的情绪贴标签。</li>
                                        <li>出现初步的同理心与安慰行为（如看到别人哭会把自己的小熊送给对方）。</li>
                                    </ul>
                                </div>
                                <div class="bg-emerald-50 border border-emerald-100 p-3 rounded-lg space-y-1 text-emerald-900">
                                    <strong>👨‍👦 奶爸育儿应对策略：</strong>
                                    <p class="leading-relaxed">广泛开展“情绪命名”。当{{this.userInfo.chlidName}}发脾气时，帮她表达出心中感受：“你现在很失望对不对？”被理解的感受能迅速平复情绪。</p>
                                </div>
                            </div>
                        </div>

                        <!-- Phase 3 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-amber-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-amber-600 text-white flex items-center justify-center text-xs">3</span>
                                    <span>阶段三：5 - 7 岁 (心理信念、内心想法与表现规则)</span>
                                </h4>
                                <span class="bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">表情掩饰与意图理解</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 情绪理解发展特征：</strong>
                                    <ul class="list-disc list-inside text-slate-600 space-y-1 leading-relaxed">
                                        <li>理解“愿望与信念”决定情绪：明白两个人面对同一种情况，因为期待不同会产生完全不同的情绪。</li>
                                        <li>掌握**情绪表达规则**：明白收到不喜欢的礼物时，为了礼貌可以在脸上佯装微笑。</li>
                                        <li>能区分“真实的内心感受”与“外表展现的表情”。</li>
                                    </ul>
                                </div>
                                <div class="bg-amber-50 border border-amber-100 p-3 rounded-lg space-y-1 text-amber-900">
                                    <strong>👨‍👦 奶爸育儿应对策略：</strong>
                                    <p class="leading-relaxed">多通过绘本讨论角色内心世界：“你觉得小熊虽然在笑，他心里真的开心吗？”鼓励孩子接纳真诚的内心情感，不强求盲目压抑。</p>
                                </div>
                            </div>
                        </div>

                        <!-- Phase 4 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-purple-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs">4</span>
                                    <span>阶段四：8 - 10 岁 (矛盾/混合情绪、道德反思与完全成熟)</span>
                                </h4>
                                <span class="bg-purple-100 text-purple-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">情绪认知终极成熟阶段</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 情绪理解发展特征（终极突破）：</strong>
                                    <ul class="list-disc list-inside text-slate-600 space-y-1 leading-relaxed">
                                        <li><strong>理解矛盾/混合情绪：</strong> 彻底明白人可以**同时对同一件事产生两种对立的情绪**（如：即将去新学校，既感到兴奋又感到害怕与离别悲伤）。</li>
                                        <li><strong>掌握认知重塑调节：</strong> 明白可以通过改变看待问题的想法来改变自己的情绪。</li>
                                        <li><strong>深化道德元情绪：</strong> 深刻体验愧疚、内疚与良心谴责，情绪理解能力正式达到成年人水平！</li>
                                    </ul>
                                </div>
                                <div class="bg-purple-50 border border-purple-100 p-3 rounded-lg space-y-1 text-purple-900">
                                    <strong>👨‍👦 奶爸育儿应对策略：</strong>
                                    <p class="leading-relaxed">做{{this.userInfo.chlidName}}的知心朋友与人生导师。当他面对复杂矛盾心态时（如又想赢又怕输），引导她表达并接纳这种复杂的心理，教她学会认知重塑。</p>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <!-- ================= TAB 10: WHO 生长曲线与评估 ================= -->
            <section id="tab-charts" ref="tab-charts" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-4">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-pen-ruler text-purple-600"></i>
                                <span>{{this.userInfo.chlidName}}孩子测量数据录入与对比分析</span>
                            </h3>
                            <p class="text-xs text-slate-500">直接录入月龄与测量数据，实时自动绘制曲线图并与 WHO 国际百分位标准比对生成智能评价。</p>
                        </div>
                        <span class="text-xs bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full font-semibold">数据录入</span>
                    </div>

                    <form id="growthEntryForm" ref="growthEntryForm" @submit.prevent="saveGrowthMeasurement($event)" class="bg-slate-50 border border-slate-200 p-4 rounded-xl text-xs space-y-3">
                        <div class="font-bold text-slate-800 text-xs">📏 新增一条发育测量数据：</div>
                        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                                <label class="block text-slate-600 font-medium mb-1">月龄 (0-36个月) *</label>
                                <input type="number" min="0" max="36" id="entryMonth" ref="entryMonth" required placeholder="如: 3" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 font-medium mb-1">体重 (kg) *</label>
                                <input type="number" step="0.1" id="entryWeight" ref="entryWeight" required placeholder="如: 6.5" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 font-medium mb-1">身高/身长 (cm) *</label>
                                <input type="number" step="0.1" id="entryHeight" ref="entryHeight" required placeholder="如: 61.5" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 font-medium mb-1">头围 (cm)</label>
                                <input type="number" step="0.1" id="entryHead" ref="entryHead" placeholder="如: 40.2" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                        </div>
                        <div class="flex justify-between items-center pt-1">
                            <span class="text-[11px] text-slate-400">对比标准：WHO 0-3岁男孩百分位数标准 (P3, P50, P97)</span>
                            <button type="submit" class="bg-purple-600 hover:bg-purple-700 text-white px-4 py-1.5 rounded-lg font-semibold transition">录入并生成分析</button>
                        </div>
                    </form>

                    <!-- Growth Summary Card -->
                    <div id="growthSummaryCard" class="bg-slate-900 text-white p-4 rounded-xl space-y-2 text-xs shadow-md">
                        <div class="flex items-center justify-between border-b border-slate-700 pb-2">
                            <span class="font-bold flex items-center gap-1.5 text-purple-300">
                                <i class="fa-solid fa-square-poll-vertical"></i>
                                <span>{{this.userInfo.chlidName}}当前生长发育评估总结</span>
                            </span>
                            <span id="latestMonthTag" ref="latestMonthTag" class="bg-purple-500/30 text-purple-200 px-2 py-0.5 rounded font-mono text-[10px]">最新月龄: {{this.latestMonth || 0}}个月</span>
                        </div>
                        <p id="growthSummaryText" ref="growthSummaryText" class="text-slate-200 leading-relaxed pt-1">
                            加载生长评价中...
                        </p>
                    </div>

                    <!-- Charts Display -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                        <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                            <h4 class="text-xs font-bold text-slate-700 mb-2 text-center">体重发育曲线对比 (kg)</h4>
                            <div class="h-64">
                                <canvas id="weightChartCanvas" ref="weightChartCanvas"></canvas>
                            </div>
                        </div>
                        <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                            <h4 class="text-xs font-bold text-slate-700 mb-2 text-center">身高/身长发育曲线对比 (cm)</h4>
                            <div class="h-64">
                                <canvas id="heightChartCanvas" ref="heightChartCanvas"></canvas>
                            </div>
                        </div>
                    </div>

                    <!-- History Growth Records Container (Matching Fetal Checkup Design) -->
                    <div class="space-y-3 pt-4 border-t border-slate-100">
                        <h4 class="font-bold text-xs text-slate-800 flex items-center gap-1.5">
                            <i class="fa-solid fa-clock-rotate-left text-purple-600"></i>
                            <span>📋 历史生长发育测量档案库：</span>
                        </h4>
                        <div id="growthHistoryContainer" ref="growthHistoryContainer" @click="delegateClick" class="space-y-3">
                            <!-- Dynamic rendered growth history cards -->
                        </div>
                    </div>
                </div>
            </section>

            <!-- ================= TAB 11: 奶爸心情微语 (随手记动态) ================= -->
            <section id="tab-mood" ref="tab-mood" class="tab-content hidden space-y-6">
                <div class="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-2xs space-y-5">

                    <!-- Section Title -->
                    <div class="flex items-center space-x-3 border-b border-slate-100 pb-4">
                        <div class="w-11 h-11 rounded-2xl bg-gradient-to-tr from-amber-400 to-rose-400 flex items-center justify-center text-white text-xl font-bold shadow-md shadow-amber-500/20">
                            <i class="fa-solid fa-heart-pulse"></i>
                        </div>
                        <div>
                            <h3 class="text-base font-extrabold text-slate-800">奶爸心情微语</h3>
                            <p class="text-xs text-slate-500 mt-0.5">随手记录育儿过程中的喜怒哀乐、瞬间感悟，支持配图与视频。</p>
                        </div>
                    </div>

                    <!-- Post Input Box -->
                    <form ref="moodPostForm" @submit.prevent="saveMoodPost()" class="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3 shadow-inner">
                        <div class="flex items-center space-x-2">
                            <span class="w-8 h-8 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center text-xs shadow-xs" id="moodInputAvatar" ref="moodInputAvatar">爸</span>
                            <span class="text-xs font-bold text-slate-700">发表一条心情随笔...</span>
                        </div>

                        <!-- Mood Tag Select & Textarea -->
                        <div class="space-y-2">
                            <textarea id="moodTextInput" ref="moodTextInput" rows="3" required placeholder="这一刻在想什么？比如：今天听了胎心音，心里踏实多了..." class="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 leading-relaxed"></textarea>
                        </div>

                        <!-- Attachments & Controls -->
                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1 border-t border-slate-200/80">
                            <div class="flex flex-wrap items-center gap-2 text-xs">
                                <!-- Mood Tag selector -->
                                <select id="moodTagSelect" ref="moodTagSelect" class="px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-700">
                                    <template v-if="moodList.length > 1">
                                        <option v-for="item in moodList" :key="item.id || item.dictName" :value="item.id">{{ item.dictName }}</option>
                                    </template>
                                    <template v-else>
                                        <option value="😊 幸福开心">😊 幸福开心</option>
                                        <option value="❤️ 期待降生">❤️ 期待降生</option>
                                        <option value="😴 累并幸福着">😴 累并幸福着</option>
                                        <option value="🥺 焦虑坦白">🥺 焦虑坦白</option>
                                        <option value="💪 能量满满">💪 能量满满</option>
                                        <option value="💡 育儿顿悟">💡 育儿顿悟</option>
                                    </template>
                                </select>

                                <!-- Upload Button -->
                                <label class="cursor-pointer bg-white border border-slate-300 hover:border-amber-400 hover:bg-amber-50 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center space-x-1.5">
                                    <i class="fa-solid fa-image text-amber-500"></i>
                                    <span>配图/视频</span>
                                    <input type="file" id="moodFileInput" ref="moodFileInput" accept="image/*,video/*" @change="handleMoodFileUpload($event)" class="hidden">
                                </label>

                                <span id="moodFilePreviewText" ref="moodFilePreviewText" class="text-[11px] text-amber-700 font-mono hidden">已选媒体</span>
                            </div>

                            <button type="submit" class="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-5 py-2 rounded-xl text-xs font-bold shadow-sm transition flex items-center justify-center space-x-1.5">
                                <span>发布微语</span>
                                <i class="fa-solid fa-paper-plane"></i>
                            </button>
                        </div>
                    </form>

                    <!-- Mood Posts Feed List -->
                    <div class="space-y-4">
                        <div class="flex justify-between items-center border-b border-slate-100 pb-2">
                            <h4 class="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                                <i class="fa-solid fa-bars-staggered text-amber-500"></i>
                                <span>全部心情动态</span>
                            </h4>
                            <span class="text-[11px] text-slate-400 font-mono">最新发布在前</span>
                        </div>

                        <div id="moodFeedContainer" ref="moodFeedContainer" @click="delegateClick" class="space-y-4">
                            <!-- Dynamic rendering of mood posts -->
                        </div>
                    </div>

                </div>
            </section>

        </div>

        <div id="icp" class="mt-auto py-4 text-center border-t border-slate-100 bg-white/60">
            <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener" class="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-sky-600 transition font-mono">
                <i class="fa-solid fa-shield-halved text-[13px]"></i>
                <span>粤ICP备2026121758-1</span>
            </a>
        </div>
    </main>

    <!-- Modal 1: New Growth Diary Modal -->
    <div id="quickAddModal" ref="quickAddModal" class="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center hidden p-4">
        <div class="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl space-y-4 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
            <button @click="closeModal('quickAddModal')" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
                <i class="fa-solid fa-xmark text-lg"></i>
            </button>

            <div class="flex items-center space-x-2 border-b border-slate-100 pb-3">
                <span class="w-8 h-8 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center text-sm font-bold">
                    <i class="fa-solid fa-book"></i>
                </span>
                <div>
                    <h3 class="text-base font-bold text-slate-800">新建时光成长日记 (支持本地多媒体上传)</h3>
                    <p class="text-[11px] text-slate-500">直接上传本地的照片、短视频或录音文件，随时记录秉宣成长。</p>
                </div>
            </div>

            <!-- Quick Template Shortcuts -->
            <div class="space-y-1">
                <label class="block text-[11px] font-bold text-slate-600">⚡ 一键应用预设日记模板：</label>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[11px]">
                    <button @click="applyWolaiTemplate('daily')" class="py-1 px-2 bg-slate-100 hover:bg-sky-50 text-slate-700 hover:text-sky-700 border border-slate-200 rounded-lg text-left transition truncate">🥣 每日育儿复盘</button>
                    <button @click="applyWolaiTemplate('checkup')" class="py-1 px-2 bg-slate-100 hover:bg-rose-50 text-slate-700 hover:text-rose-700 border border-slate-200 rounded-lg text-left transition truncate">🏥 孕期产检随记</button>
                    <button @click="applyWolaiTemplate('milestone')" class="py-1 px-2 bg-slate-100 hover:bg-amber-50 text-slate-700 hover:text-amber-700 border border-slate-200 rounded-lg text-left transition truncate">🏆 首次里程碑</button>
                    <button @click="applyWolaiTemplate('voice')" class="py-1 px-2 bg-slate-100 hover:bg-purple-50 text-slate-700 hover:text-purple-700 border border-slate-200 rounded-lg text-left transition truncate">🎙️ 爸爸声音日记</button>
                </div>
            </div>

            <!-- Form Inputs -->
            <form id="diaryEntryForm" ref="diaryEntryForm" @submit.prevent="saveNewDiary($event)">
            <div class="space-y-3 text-xs">
                <div>
                    <label class="block font-medium text-slate-700 mb-1">日记标题 *</label>
                    <input type="text" id="diaryTitleInput" ref="diaryTitleInput" required placeholder="请输入日记标题..." class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500 font-bold text-slate-800">
                </div>

                <div class="grid grid-cols-2 gap-2">
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">分类标签 *</label>
                        <select id="diaryCategoryInput" ref="diaryCategoryInput" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500">
                            <option v-for="(item, index) in diaryList" :key="item.id || item.dictName" :value="item.id" :selected="index === 0">{{ item.dictName }}</option>
                        </select>
                    </div>
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">记录日期 *</label>
                        <input type="date" id="diaryDateInput" ref="diaryDateInput" required @click="onDatePickerClick($event)" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500 cursor-pointer cursor-pointer">
                    </div>
                </div>

                
                <div>
                    <label class="block font-medium text-slate-700 mb-1">日记正文内容 *</label>
                    <textarea id="diaryContentInput" ref="diaryContentInput" rows="5" required placeholder="记录秉宣今天的成长瞬间、护理总结或心里话..." class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500 leading-relaxed"></textarea>
                </div>

                <!-- Local File Upload Box -->
                <div class="bg-sky-50/60 border-2 border-dashed border-sky-200 p-3.5 rounded-xl space-y-2">
                    <label class="block font-bold text-sky-900 text-xs flex items-center justify-between">
                        <span class="flex items-center gap-1.5">
                            <i class="fa-solid fa-cloud-arrow-up text-sky-600"></i>
                            <span>上传本地图片/短视频/录音附件：</span>
                        </span>
                        <span class="text-[10px] text-slate-400 font-normal">支持照片、.mp4等</span>
                    </label>
                    
                    <input type="file" id="diaryLocalFileInput" ref="diaryLocalFileInput" accept="image/*,video/*,audio/*" @change="handleLocalFileUpload($event)" class="w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-sky-600 file:text-white hover:file:bg-sky-700 cursor-pointer">

                    <div id="localFilePreviewBadge" ref="localFilePreviewBadge" class="hidden bg-white p-2 rounded-lg border border-sky-200 text-[11px] text-slate-700 flex items-center justify-between font-mono">
                        <span id="localFileNameText" ref="localFileNameText" class="truncate max-w-xs">已选择文件</span>
                        <span id="localFileTypeTag" ref="localFileTypeTag" class="bg-sky-100 text-sky-800 px-2 py-0.5 rounded font-bold">照片</span>
                    </div>
                </div>

                <!-- Web URL & Voice simulation optional inputs -->
                <div class="grid grid-cols-2 gap-2">
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">网络媒体链接 (可选)</label>
                        <input type="text" id="diaryMediaInput" ref="diaryMediaInput" placeholder="https://..." class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs">
                    </div>
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">语音时长 (如 01:30)</label>
                        <div class="flex space-x-1">
                            <input type="text" id="diaryAudioInput" ref="diaryAudioInput" placeholder="如 01:30" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs">
                            <button type="button" @click="simulateVoiceRecord()" :class="isRecording ? 'bg-rose-100 hover:bg-rose-200 text-rose-700 px-2 py-1 rounded text-[11px] whitespace-nowrap font-semibold animate-pulse' : 'bg-purple-100 hover:bg-purple-200 text-purple-700 px-2 py-1 rounded text-[11px] whitespace-nowrap font-semibold'">{{ isRecording ? '停止' : '录音' }}</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button type="button" @click="closeModal('quickAddModal')" class="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-600">取消</button>
                <button type="submit" class="px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-semibold shadow-xs">保存日记</button>
            </div>
            </form>
        </div>
    </div>

    <!-- Modal 2: Read Diary Detail Modal -->
    <div id="diaryDetailModal" ref="diaryDetailModal" class="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center hidden p-4">
        <div class="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl space-y-4 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
            <button @click="closeModal('diaryDetailModal')" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
                <i class="fa-solid fa-xmark text-lg"></i>
            </button>

            <!-- Header info -->
            <div class="space-y-1.5 border-b border-slate-100 pb-3">
                <div class="flex items-center space-x-2">
                    <span id="detailCategoryTag" ref="detailCategoryTag" class="bg-sky-100 text-sky-800 text-xs px-2.5 py-0.5 rounded-full font-bold">日常</span>
                    <span id="detailDate" ref="detailDate" class="text-xs text-slate-400 font-mono">2026-08-15</span>
                </div>
                <h3 id="detailTitle" ref="detailTitle" class="text-lg font-extrabold text-slate-800">日记标题</h3>
            </div>

            <!-- Main text body -->
            <div id="detailContent" ref="detailContent" class="text-xs sm:text-sm text-slate-700 whitespace-pre-line leading-relaxed font-sans bg-slate-50 p-4 rounded-xl border border-slate-200/80">
                日记正文内容...
            </div>

            <!-- Media Preview (Image or Video) -->
            <div id="detailMediaBox" ref="detailMediaBox" class="hidden space-y-1">
                <label class="block text-xs font-bold text-slate-700">📷 附件媒体：</label>
                <div class="rounded-xl overflow-hidden bg-slate-900 border border-slate-200 max-h-80 flex items-center justify-center">
                    <img id="detailImg" ref="detailImg" src="" alt="Diary media" class="max-h-80 max-w-full object-contain hidden">
                    <video id="detailVideo" ref="detailVideo" controls src="" class="max-h-80 max-w-full object-contain hidden"></video>
                </div>
            </div>

            <!-- Audio Player Box -->
            <div id="detailAudioBox" ref="detailAudioBox" class="hidden bg-purple-50 border border-purple-200 p-3 rounded-xl space-y-2">
                <div class="flex items-center justify-between">
                    <span class="text-xs font-bold text-purple-900 flex items-center gap-1.5">
                        <i class="fa-solid fa-microphone text-purple-600"></i>
                        <span>奶爸声音留声机</span>
                    </span>
                    <span id="detailAudioDuration" ref="detailAudioDuration" class="text-[10px] bg-purple-200 text-purple-800 px-2 py-0.5 rounded font-mono font-bold">01:15</span>
                </div>
                <audio id="detailAudioPlayer" ref="detailAudioPlayer" controls class="w-full hidden"></audio>
                <button @click="playDetailVoice()" class="w-full py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-semibold transition flex items-center justify-center space-x-1">
                    <i class="fa-solid fa-volume-high"></i>
                    <span>播放声音留声机</span>
                </button>
            </div>

            <!-- Footer actions -->
            <div class="flex justify-between items-center pt-2 border-t border-slate-100">
                <button id="deleteDetailBtn" ref="deleteDetailBtn" class="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg text-xs font-semibold transition">删除此日记</button>
                <button @click="closeModal('diaryDetailModal')" class="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold transition">关闭</button>
            </div>
        </div>
    </div>

    <!-- Modal 3: Add Media to Growth Album Modal -->
    <div id="addAlbumMediaModal" ref="addAlbumMediaModal" class="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center hidden p-4">
        <div class="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
            <button @click="closeModal('addAlbumMediaModal')" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
                <i class="fa-solid fa-xmark text-lg"></i>
            </button>

            <div class="flex items-center space-x-2 border-b border-slate-100 pb-3">
                <span class="w-8 h-8 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center text-sm font-bold">
                    <i class="fa-solid fa-images"></i>
                </span>
                <div>
                    <h3 class="text-base font-bold text-slate-800">添加影像素材到秉宣相册</h3>
                    <p class="text-[11px] text-slate-500">上传本地图片或短视频，按主题归档。</p>
                </div>
            </div>

            <form id="albumMediaForm" ref="albumMediaForm" @submit.prevent="saveNewAlbumMedia()">
            <div class="space-y-3 text-xs">
                <div>
                    <label class="block font-medium text-slate-700 mb-1">素材标题/简介 *</label>
                    <input type="text" id="albumMediaTitleInput" ref="albumMediaTitleInput" required placeholder="例如：18周B超小脸照 / 秉宣第一次抬头" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-teal-500 font-semibold">
                </div>

                <div class="grid grid-cols-2 gap-2">
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">主题分类 *</label>
                        <select id="albumMediaTagInput" ref="albumMediaTagInput" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-teal-500">
                            <template v-if="albumList.length > 0">
                                <option v-for="item in albumList" :key="item.id" :value="item.id">{{ item.dictName }}</option>
                            </template>
                        </select>
                    </div>
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">拍摄日期 *</label>
                        <input type="date" id="albumMediaDateInput" ref="albumMediaDateInput" required @click="onDatePickerClick($event)" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-teal-500 cursor-pointer">
                    </div>
                </div>

                <!-- Local File Upload Box for Album -->
                <div class="bg-teal-50/60 border-2 border-dashed border-teal-200 p-3.5 rounded-xl space-y-2">
                    <label class="block font-bold text-teal-900 text-xs flex items-center justify-between">
                        <span class="flex items-center gap-1.5">
                            <i class="fa-solid fa-cloud-arrow-up text-teal-600"></i>
                            <span>选择本地图片或视频上传：</span>
                        </span>
                        <span class="text-[10px] text-slate-400 font-normal">支持 .jpg, .png, .mp4, .mov</span>
                    </label>
                    
                    <input type="file" id="albumLocalFileInput" ref="albumLocalFileInput" accept="image/*,video/*" @change="handleAlbumLocalFileUpload($event)" class="w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-teal-600 file:text-white hover:file:bg-teal-700 cursor-pointer">

                    <div id="albumLocalFileBadge" ref="albumLocalFileBadge" class="hidden bg-white p-2 rounded-lg border border-teal-200 text-[11px] text-slate-700 flex items-center justify-between font-mono">
                        <span id="albumLocalFileNameText" ref="albumLocalFileNameText" class="truncate max-w-xs">已选择文件</span>
                        <span id="albumLocalFileTypeTag" ref="albumLocalFileTypeTag" class="bg-teal-100 text-teal-800 px-2 py-0.5 rounded font-bold">照片</span>
                    </div>
                </div>

                <div>
                    <label class="block font-medium text-slate-700 mb-1">网络链接 (可选替代方案)</label>
                    <input type="text" id="albumMediaUrlInput" ref="albumMediaUrlInput" placeholder="https://..." class="w-full px-3 py-2 border border-slate-300 rounded-lg">
                </div>
            </div>

            <div class="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button type="button" @click="closeModal('addAlbumMediaModal')" class="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-600">取消</button>
                <button type="submit" class="px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-semibold shadow-xs">存入成长相册</button>
            </div>
            </form>
        </div>
    </div>

    <!-- Modal 4: Media Lightbox View Modal -->
    <div id="mediaLightboxModal" ref="mediaLightboxModal" class="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center hidden p-4">
        <div class="relative max-w-4xl w-full flex flex-col items-center justify-center space-y-3">
            <button @click="closeModal('mediaLightboxModal')" class="absolute -top-10 right-0 text-white hover:text-rose-400 text-2xl">
                <i class="fa-solid fa-xmark"></i>
            </button>

            <!-- Media Container -->
            <div class="w-full max-h-[75vh] flex items-center justify-center rounded-2xl overflow-hidden bg-black/60 border border-slate-800">
                <img id="lightboxImg" ref="lightboxImg" src="" alt="Album Lightbox" class="max-h-[75vh] max-w-full object-contain hidden">
                <video id="lightboxVideo" ref="lightboxVideo" controls src="" class="max-h-[75vh] max-w-full object-contain hidden"></video>
            </div>

            <!-- Media Details Label -->
            <div class="bg-slate-900/90 border border-slate-800 text-white p-3.5 rounded-xl w-full flex items-center justify-between text-xs font-sans">
                <div class="space-y-0.5">
                    <div class="flex items-center space-x-2">
                        <span id="lightboxTag" ref="lightboxTag" class="bg-teal-500/20 text-teal-300 border border-teal-500/30 px-2 py-0.5 rounded text-[10px] font-mono">孕期超声</span>
                        <h4 id="lightboxTitle" ref="lightboxTitle" class="font-bold text-sm text-slate-100">素材标题</h4>
                    </div>
                    <span id="lightboxDate" ref="lightboxDate" class="text-[11px] text-slate-400 font-mono">拍摄时间: 2026-08-10</span>
                </div>
                <button id="lightboxDeleteBtn" ref="lightboxDeleteBtn" class="text-rose-400 hover:text-rose-300 text-xs px-3 py-1 bg-rose-500/20 hover:bg-rose-500/30 rounded-lg transition">删除此素材</button>
            </div>
        </div>
    </div>

    <!-- Modal 5: Site & Profile Settings Modal -->
    <div id="siteSettingsModal" ref="siteSettingsModal" class="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center hidden p-4">
        <div class="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 relative">
            <button @click="closeModal('siteSettingsModal')" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
                <i class="fa-solid fa-xmark text-lg"></i>
            </button>

            <div class="flex items-center space-x-2 border-b border-slate-100 pb-3">
                <span class="w-8 h-8 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center text-sm font-bold">
                    <i class="fa-solid fa-sliders"></i>
                </span>
                <div>
                    <h3 class="text-base font-bold text-slate-800">个人信息与网站设置</h3>
                    <p class="text-[11px] text-slate-500">自定义奶爸名称、宝宝名称、网站标题与头像。</p>
                </div>
            </div>

            <div class="space-y-3 text-xs">
                <div>
                    <label class="block font-medium text-slate-700 mb-1">网站标题 *</label>
                    <input type="text" id="settingsSiteTitleInput" ref="settingsSiteTitleInput" placeholder="如：王秉宣的成长纪事" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500 font-bold">
                </div>

                <div class="grid grid-cols-2 gap-2">
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">奶爸昵称 *</label>
                        <input type="text" id="settingsDadNameInput" ref="settingsDadNameInput" placeholder="如：独爱秉宣的奶爸" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500">
                    </div>
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">宝宝姓名 *</label>
                        <input type="text" id="settingsBabyNameInput" ref="settingsBabyNameInput" placeholder="如：王秉宣" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500">
                    </div>
                </div>

                <div>
                    <label class="block font-medium text-slate-700 mb-1">奶爸状态标签</label>
                    <input type="text" id="settingsDadStatusInput" ref="settingsDadStatusInput" placeholder="如：奶爸备养中 / 宣宣快乐成长中" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500">
                </div>

                <!-- Custom Avatar Upload -->
                <div class="bg-slate-50 border border-slate-200 p-3 rounded-xl space-y-2">
                    <label class="block font-bold text-slate-700 text-xs">🖼️ 奶爸头像设置：</label>
                    <div class="flex items-center space-x-3">
                        <div id="settingsAvatarPreview" ref="settingsAvatarPreview" class="w-12 h-12 rounded-2xl bg-sky-500 text-white flex items-center justify-center font-bold text-lg overflow-hidden border border-slate-200">
                            <i class="fa-solid fa-baby"></i>
                        </div>
                        <div class="flex-1 space-y-1">
                            <label class="cursor-pointer bg-white border border-slate-300 hover:bg-sky-50 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium transition inline-block">
                                <i class="fa-solid fa-upload mr-1 text-sky-600"></i> 上传自定义头像
                                <input type="file" accept="image/*" @change="handleAvatarUpload($event)" class="hidden">
                            </label>
                            <button @click="resetAvatarToDefault()" type="button" class="text-[11px] text-slate-400 hover:text-rose-500 underline block">恢复默认图标</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button @click="closeModal('siteSettingsModal')" class="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-600">取消</button>
                <button @click="saveSiteSettings()" class="px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-semibold shadow-xs">保存修改</button>
            </div>
        </div>
    </div>

    <!-- Modal 6: Login Modal -->
    <div id="loginModal" ref="loginModal" class="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center hidden p-4">
        <div class="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 relative">
            <button @click="closeModal('loginModal')" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
                <i class="fa-solid fa-xmark text-lg"></i>
            </button>

            <div class="flex items-center space-x-2 border-b border-slate-100 pb-3">
                <span class="w-8 h-8 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center text-sm font-bold">
                    <i class="fa-solid fa-user-lock"></i>
                </span>
                <div>
                    <h3 class="text-base font-bold text-slate-800">登录编辑中心</h3>
                    <p class="text-[11px] text-slate-500">验证身份后方可对宝宝成长记录进行编辑。</p>
                </div>
            </div>

            <!-- Privacy & Authorship Warning -->
            <div class="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2.5">
                <i class="fa-solid fa-shield-halved text-amber-600 mt-0.5"></i>
                <div class="text-[11px] text-amber-900 leading-relaxed space-y-1">
                    <p class="font-bold">⚠️ 仅允许本人（宝宝直系监护人）操作</p>
                    <p>本系统记录包含王秉宣小朋友的影像、成长数据及奶爸私人感悟，属于家庭成员隐私资产。为保护宝宝肖像权与家庭数据安全，<strong>非本人请勿尝试登录或编辑</strong>，所有编辑操作都应获得奶爸本人授权确认。</p>
                </div>
            </div>

            <!-- Login Form -->
            <form id="loginForm" ref="loginForm" @submit.prevent="handleLogin">
            <div class="space-y-3 text-xs">
                <div>
                    <label class="block font-medium text-slate-700 mb-1">账号 *</label>
                    <input
                        v-model="loginAccount"
                        type="text"
                        required
                        placeholder="请输入账号..."
                        class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500"
                    >
                </div>
                <div>
                    <label class="block font-medium text-slate-700 mb-1">密码 *</label>
                    <input
                        v-model="loginPassword"
                        type="password"
                        required
                        placeholder="请输入密码..."
                        class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500"
                    >
                </div>
            </div>

            <div class="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button type="button" @click="closeModal('loginModal')" class="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-600">取消</button>
                <button type="submit" class="px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-semibold shadow-xs">
                    <i class="fa-solid fa-right-to-bracket mr-1.5"></i>登录
                </button>
            </div>
            </form>
        </div>
    </div>
  </div>
</template>

<script>
/* global Chart */
import './assets/scripts/chart.js'
import './assets/scripts/tailwind.js'
import './assets/scripts/tailwind.config.js'
import * as indexjs from './assets/libs/index.js'
import { setToken, isTokenValid } from './assets/libs/storage.js'
import { upload_image, resolveMediaUrl, getDictValuesByField } from './assets/libs/utils.js'

// Global Application State Object
let weightChart = null;
let heightChart = null;

export default {
    name: 'App',
    data() {
        return {
            userInfo: {},
            siteTitle: "王秉宣的成长纪事",
            dadName: "独爱秉宣的奶爸",
            babyName: "王秉宣",
            dadStatus: "奶爸备养中",
            
            avatarUrl: null,
            avatarPath: null, // 头像相对路径（用于存库）
            isRecording: false, // 是否正在录音
            mediaRecorder: null, // MediaRecorder 实例
            recordStartTs: 0, // 录音开始时间戳
            dueDate: "2027-06-15",
            embryoType: "D5",
            transferDate: "2026-09-27",
            currentDiaryCategory: "ALL",
            currentFoodMonth: 1,
            uploadedLocalFile: null,
            orgtreeList: [], // 多级字典（组织树）
            dailyList: [], // 日常护理记录列表
            diaryList: [], // 时光成长日记记录列表
            albumList: [], // 时光成长相册记录列表
            moodList: [], // 心情微语记录列表
            pregnancyList: [], // 预产期推算算法记录列表
            maxSize: 10000000, // 全部显示的最大数
            latestMonth: 0, // 最新月龄，用于显示在页面上
            // p50 WHO数据 用于charts图表显示
            p50months : [0, 1, 2, 3, 4, 5, 6, 9, 12, 18, 24, 36],
            p50Weight : [3.3, 4.5, 5.6, 6.4, 7.0, 7.5, 7.9, 8.9, 9.6, 10.9, 12.2, 14.3], 
            p50Height : [49.9, 54.7, 58.4, 61.4, 63.9, 65.9, 67.6, 72.0, 75.7, 82.3, 87.8, 96.1],

            // Mood Posts state
            uploadedMoodFile: null,

            // Login Modal state
            loginAccount: "",
            loginPassword: "",
            isLoggedIn: false,

            moodPosts: [
                // {
                //     id: 301,
                //     text: "今天去医院做产检听到了秉宣的胎心音，每分钟148下！那强有力的‘咚咚咚’声真的让我眼眶泛红。虽然备产辛苦，但这一刻觉得一切都值得！",
                //     tag: "❤️ 期待降生",
                //     time: "2026-08-17 14:20",
                //     media: "https://images.unsplash.com/photo-1555252333-9f8e92e65df9?w=800&auto=format&fit=crop&q=60",
                //     mediaType: "image",
                //     likes: 1
                // },
                // {
                //     id: 302,
                //     text: "深夜看完了一本关于回避型依恋重塑的书，做了一些笔记。提醒自己：以后秉宣哭闹时，一定要在3秒内给他温暖的拥抱，绝不走我小时候受过的冷暴力老路！",
                //     tag: "💪 能量满满",
                //     time: "2026-08-16 23:45",
                //     media: null,
                //     mediaType: null,
                //     likes: 0
                // }
            ],

            // Growth Album state
            currentAlbumType: "ALL",
            currentAlbumTag: "ALL",
            uploadedAlbumLocalFile: null,

            albumMediaList: [
                // {
                //     id: 201,
                //     title: "18周大排畸B超剪影",
                //     tag: "孕期超声",
                //     type: "image",
                //     date: "2026-09-22",
                //     yearMonth: "2026年9月",
                //     url: "https://images.unsplash.com/photo-1555252333-9f8e92e65df9?w=800&auto=format&fit=crop&q=60"
                // },
                // {
                //     id: 202,
                //     title: "爸爸为秉宣准备的第一个小熊玩偶",
                //     tag: "日常萌照",
                //     type: "image",
                //     date: "2026-08-15",
                //     yearMonth: "2026年8月",
                //     url: "https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=800&auto=format&fit=crop&q=60"
                // }
            ],

            // Fetal checkup history records
            fetalRecords: [
                // {
                //     id: 1,
                //     date: "2026-08-10",
                //     week: "12周+3天",
                //     heartRate: 155,
                //     weight: 55,
                //     bpd: 21.0,
                //     fl: 9.5,
                //     ac: 62.0,
                //     amniotic: 38,
                //     doctorSummary: "NT早期结构筛查顺利通过！NT厚度1.2mm指标优秀，胎儿动作活跃，胎心率规律无异常。"
                // },
                // {
                //     id: 2,
                //     date: "2026-09-22",
                //     week: "18周+5天",
                //     heartRate: 146,
                //     weight: 230,
                //     bpd: 42.5,
                //     fl: 27.2,
                //     ac: 132.0,
                //     amniotic: 45,
                //     doctorSummary: "中期唐氏筛查低风险，器官大体结构发育良好。建议奶爸提醒妈妈多补充蛋白质与高钙食品。"
                // }
            ],

            // Daily tracker quick logs
            trackerLogs: [
                // { id: 1, type: "配方奶", content1: "吸吮有力，拍打顺利排出气体", content2: "90 ml", content3: "喂奶速记", content4: "喂奶", createTime: "2026-08-17 08:30", content5: "🍼" },
                // { id: 2, type: "便便", content1: "无红屁屁，清洗后涂抹护臀膏", content2: "黄色糊状", content3: "换尿布", content4: "换尿布", createTime: "2026-08-17 09:15", content5: "💩" },
                // { id: 3, type: "补充维生素D3", content1: "每日常规补充", content2: "400IU", content3: "用药打卡", content4: "用药", createTime: "2026-08-17 10:00", content5: "☀️" },
                // { id: 4, type: "疫苗接种", content1: "深圳市卫健委安排顺利接种", content2: "乙肝第一针", content3: "疫苗接种", content4: "疫苗", createTime: "2026-08-17 11:20", content5: "💉" }
            ],

            // Diaries DB
            diaries: [
                // {
                //     id: 101,
                //     title: "给秉宣写的第一封信：欢迎你准备来到这个世界",
                //     category: "孕期",
                //     date: "2026-08-15",
                //     yearMonth: "2026年8月",
                //     content: "宣宣，爸爸提前为你想好了名字——王秉宣。秉持正气，宣扬善良。我会倾尽全力给你双倍的爱！今天去查了孕期资料，把你的小卡片建立好了，明年6月等你平安出生！\n\n📌 参照 AAP 孕期护理标准准备待产包。",
                //     media: "https://images.unsplash.com/photo-1555252333-9f8e92e65df9?w=800&auto=format&fit=crop&q=60",
                //     mediaType: "image",
                //     audioDuration: "01:15"
                // },
                // {
                //     id: 102,
                //     title: "奶爸学习笔记：0-3个月必备的抱姿与拍嗝技巧",
                //     category: "奶爸感悟",
                //     date: "2026-08-16",
                //     yearMonth: "2026年8月",
                //     content: "今天系统学习了拍嗝知识！新手爸妈一定要记住：\n1. 喂完奶后将宝宝竖抱，头部靠在奶爸肩头。\n2. 手掌空心拱起，由下往上轻拍背部。\n如果不拍嗝容易吐奶引发窒息风险。另外研究了 AAP 睡眠安全，绝对要同室不同床！",
                //     media: null,
                //     mediaType: null,
                //     audioDuration: null
                // }
            ],

            // 4 Months Solid Food Plan Database (120 days = 40 3-day blocks)
            foodStatus: [], // 食物状态
            foodPlanMonths: {
                1: [
                    {dayRange: "第 1-3 天", food: "高铁强化米粉 (单一冲调)", texture: "稀糊状 (1勺米粉+4勺温水)", note: "连续观察3天皮疹与腹泻，每日1次，上午10点喂。" },
                    {dayRange: "第 4-6 天", food: "高铁米粉 + 细腻土豆泥", texture: "蒸熟压烂取泥，混合米粉", note: "观察3天，补充优质碳水与钾元素。" },
                    {dayRange: "第 7-9 天", food: "高铁米粉 + 细腻南瓜泥", texture: "蒸熟去皮压泥，温和易吸收", note: "天然微甜易接受，促进胃肠蠕动。" },
                    {dayRange: "第 10-12 天", food: "高铁米粉 + 胡萝卜蒸泥", texture: "去皮切片蒸透捣泥", note: "富含胡萝卜素，保护视力与肠道黏膜。" },
                    {dayRange: "第 13-15 天", food: "高铁米粉 + 焯水菠菜泥", texture: "水焯去除草酸后研磨成细泥", note: "补充植物铁质与丰富叶酸。" },
                    {dayRange: "第 16-18 天", food: "高铁米粉 + 高铁猪肉泥/猪肝泥", texture: "煮透研磨细腻肉泥", note: "极度重要！补充6月龄后自身消耗掉的血红素铁。" },
                    {dayRange: "第 19-21 天", food: "高铁米粉 + 甜小油菜泥", texture: "取嫩叶焯水打成泥", note: "补充膳食纤维，保持大便通畅。" },
                    {dayRange: "第 22-24 天", food: "高铁米粉 + 苹果刮泥/蒸苹果泥", texture: "鲜苹果刮细泥或蒸泥", note: "酸甜口感适度刺激味觉萌发。" },
                    {dayRange: "第 25-27 天", food: "高铁米粉 + 铁棍山药泥", texture: "去皮蒸熟压细泥", note: "健脾养胃，质地顺滑好吞咽。" },
                    {dayRange: "第 28-30 天", food: "高铁米粉 + 红薯/紫薯蒸泥", texture: "蒸软去皮捣极细泥", note: "补充微量元素，预防婴儿便秘。" }
                ],
                2: [
                    {dayRange: "第 31-33 天", food: "牛肉胡萝卜复合泥", texture: "细牛肉与胡萝卜同蒸捣泥", note: "每日1-2次，每次大半碗，高密度补铁。" },
                    {dayRange: "第 34-36 天", food: "深海鳕鱼山药泥", texture: "去刺清蒸捣碎", note: "富含DHA与蛋白质，严格观察海鲜过敏。" },
                    {dayRange: "第 37-39 天", food: "熟蛋黄泥 (1/4个起)", texture: "水煮蛋剥取1/4研碎混温水", note: "蛋白易过敏，8个月前仅吃熟蛋黄。" },
                    {dayRange: "第 40-42 天", food: "熟蛋黄泥 (1/2个)", texture: "蛋黄研碎混合米粉", note: "逐步增加蛋黄比例，补充卵磷脂。" },
                    {dayRange: "第 43-45 天", food: "鸡肉菠菜糊", texture: "去皮鸡胸肉蒸熟+菠菜泥", note: "丰富优质蛋白与多种维生素搭配。" },
                    {dayRange: "第 46-48 天", food: "鲜虾番茄泥", texture: "去虾线清蒸打泥+番茄蒸泥", note: "鲜香开胃，促进铁与钙吸收。" },
                    {dayRange: "第 49-51 天", food: "豆腐南瓜细腻稠泥", texture: "植物嫩豆腐+南瓜同蒸", note: "优质植物蛋白，质地软嫩。" },
                    {dayRange: "第 52-54 天", food: "猪肉冬瓜碎米糊", texture: "质地稍增厚，带微小软颗粒", note: "引导舌头前后推挤小颗粒。" },
                    {dayRange: "第 55-57 天", food: "熟蛋黄泥 (1个完整蛋黄)", texture: "整颗蛋黄加水或米汤研糊", note: "完全解锁整颗蛋黄摄入。" },
                    {dayRange: "第 58-60 天", food: "三鲜复合泥 (牛肉+胡萝卜+西蓝花)", texture: "蒸熟软碎块复合泥", note: "第2个月辅食顺利过渡！" }
                ],
                3: [
                    {dayRange: "第 61-63 天", food: "碎碎小烂粥 + 猪肉碎末", texture: "稠厚小颗粒烂粥", note: "锻炼牙龈咬合与舌头上下抵压能力。" },
                    {dayRange: "第 64-66 天", food: "软小面条 + 鸡肉南瓜汤", texture: "碎切小段软面条，无盐清汤", note: "主食多样化，口感丰富。" },
                    {dayRange: "第 67-69 天", food: "熟蒸胡萝卜条/西蓝花朵 (手指食物)", texture: "软熟长条状 (约5cm)", note: "引导宝宝用手抓取自主进食，锻炼手眼协调。" },
                    {dayRange: "第 70-72 天", food: "鳕鱼碎末小烂粥 + 碎青菜", texture: "稠烂粥配细碎鱼肉粒", note: "培养独立进食兴趣。" },
                    {dayRange: "第 73-75 天", food: "软蒸苹果块 / 香蕉段", texture: "软熟小块，一捏即软", note: "水果手指食物尝试，捏取吞咽。" },
                    {dayRange: "第 76-78 天", food: "牛肉菠菜烂碎面", texture: "小段短面条粒", note: "咀嚼肌发力锻炼。" },
                    {dayRange: "第 79-81 天", food: "软豆腐土豆饼", texture: "煎/蒸软软的小薄饼", note: "手抓咀嚼练习，香软易消化。" },
                    {dayRange: "第 82-84 天", food: "虾仁南瓜碎碎粥", texture: "虾仁切碎泥与南瓜小粒烂粥", note: "补充矿物质与天然清甜味。" },
                    {dayRange: "第 85-87 天", food: "猪肝西蓝花微颗粒糊", texture: "小颗粒状蒸碎糊", note: "持续补铁防贫血。" },
                    {dayRange: "第 88-90 天", food: "蛋黄红薯小饭团", texture: "软烂小饭团手抓食", note: "第3个月顺利完成！" }
                ],
                4: [
                    {dayRange: "第 91-93 天", food: "软小肉丸 (牛肉/猪肉)", texture: "蒸熟软嫩易捏碎小丸子", note: "用手抓咬肉丸，感受肉质纤维。" },
                    {dayRange: "第 94-96 天", food: "蔬菜蛋黄蒸糕", texture: "胡萝卜/西蓝花+蛋黄蒸糕切块", note: "松软手抓食物，营养均衡。" },
                    {dayRange: "第 97-99 天", food: "鲜虾豆腐手指条", texture: "鲜虾豆腐小指条", note: "高钙与蛋白组合，自如抓咬。" },
                    {dayRange: "第 100-102 天", food: "无盐小馄饨 (鲜肉蔬菜馅)", texture: "小皮软馄饨，剪成半粒", note: "感受面皮与馅料混合口感。" },
                    {dayRange: "第 103-105 天", food: "番茄牛肉软烂米饭", texture: "从稀粥过渡到软烂小烩饭", note: "逐步建立一日三餐主食习惯。" },
                    {dayRange: "第 106-108 天", food: "鳕鱼蔬菜小软饼", texture: "软嫩薄饼，自由抓嚼", note: "练习门牙咬断与槽齿咀嚼。" },
                    {dayRange: "第 109-111 天", food: "鸡肉紫菜小汤面", texture: "碎面条+碎紫菜+去皮鸡肉末", note: "丰富风味感知。" },
                    {dayRange: "第 112-114 天", food: "杂粮南瓜软饭 + 蒸肉饼", texture: "软烂饭配小碎肉饼", note: "接近家庭共餐食物形态。" },
                    {dayRange: "第 115-117 天", food: "菠菜猪肝蒸蛋黄软糕", texture: "营养密度极高的小软糕", note: "全方位补充铁、锌、维A。" },
                    {dayRange: "第 118-120 天", food: "多样化家庭共享软餐 (虾仁蒸蛋+软米饭+焯青菜)", texture: "家庭软餐同桌进食", note: "🎉 恭喜！顺利完成4个月（120天）辅食解锁全打卡！" }
                ]
            },

            growthHistory: [ // WHO 生长曲线记录
                // { month: 0, weight: 3.4, height: 50.0, head: 34.5 },
                // { month: 1, weight: 4.6, height: 54.5, head: 37.0 },
                // { month: 2, weight: 5.8, height: 58.2, head: 39.0 },
                // { month: 3, weight: 6.7, height: 61.8, head: 40.5 }
            ]
        };
    },
    watch: {
        // 网站标题变化时同步浏览器标签页 title
        siteTitle(val) {
            document.title = val;
        }
    },
    methods: {
    // Modal Helpers
    openModal(modalId) {
        const el = this.$refs[modalId];
        if (el) el.classList.remove('hidden');
        if (modalId === 'loginModal') {
            this.loginAccount = "";
            this.loginPassword = "";
        }
    },
    /**
     * 带登录守卫的弹窗打开：
     * - 已登录：正常弹出指定弹窗
     * - 未登录：弹出 loginModal 登录框
     */
    openModalGuarded(modalId) {
        if (!this.requireLogin()) return;
        this.openModal(modalId);
    },
    closeModal(modalId) {
        const el = this.$refs[modalId];
        if (el) el.classList.add('hidden');
        if (modalId === 'loginModal') {
            this.loginAccount = "";
            this.loginPassword = "";
        }
        // 关闭日记弹窗时若仍在录音，停止并释放麦克风
        if (modalId === 'quickAddModal' && this.isRecording) {
            if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
                this.mediaRecorder.stop();
            }
            this.isRecording = false;
        }
    },

    /**
     * 轻提示：顶部浮层，自动消失，不阻塞操作。
     * type: 'success' | 'error' | 'info'；duration: 毫秒，默认 1500
     */
    showToast(msg, type = 'info', duration = 1500) {
        const colors = {
            success: 'bg-emerald-600',
            error: 'bg-rose-600',
            info: 'bg-slate-800'
        };
        const icons = {
            success: 'fa-circle-check',
            error: 'fa-circle-exclamation',
            info: 'fa-circle-info'
        };

        let wrap = document.getElementById('appToastWrap');
        if (!wrap) {
            wrap = document.createElement('div');
            wrap.id = 'appToastWrap';
            wrap.className = 'fixed top-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-2 pointer-events-none';
            document.body.appendChild(wrap);
        }

        const el = document.createElement('div');
        el.className = `${colors[type] || colors.info} text-white text-xs font-medium px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 max-w-[90vw] opacity-0 transition-all duration-300`;
        el.innerHTML = `<i class="fa-solid ${icons[type] || icons.info}"></i><span></span>`;
        el.querySelector('span').innerText = msg;
        wrap.appendChild(el);

        requestAnimationFrame(() => {
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
        });

        setTimeout(() => {
            el.style.opacity = '0';
            el.style.transform = 'translateY(-8px)';
            setTimeout(() => {
                el.remove();
                if (wrap && !wrap.children.length) wrap.remove();
            }, 300);
        }, duration);
    },

    /**
     * 登录按钮逻辑。
     * 目前仅做前端占位：验证账号密码是否非空，并设置 isLoggedIn 标志位。
     * 后续接入真实后端鉴权时，替换为 fetch/axios 调用即可。
     */
    handleLogin() {
        const username = (this.loginAccount || "").trim();
        const password = (this.loginPassword || "").trim();
        if (!username || !password) {
            // 兜底：正常情况下表单 required 已由浏览器在输入框下方弹出提示
            const form = this.$refs.loginForm;
            if (form && form.reportValidity) form.reportValidity();
            return;
        }
        indexjs.login({ 'username': username, 'password': password }).then((res) => {
            // handleResponse 返回的是后端 JSON body，业务成功标识为 code === 200
            if (res && res.code === 200) {
                // 保存 token，供 request.js 后续请求注入 Authorization
                const token = res.token || (res.data && res.data.token);
                if (token) setToken(token);
                this.isLoggedIn = true;
                this.showToast(`登录成功！欢迎回来，${username}`, 'success');
                this.closeModal('loginModal');
            } else {
                this.showToast((res && res.msg) ? res.msg : '登录失败，请检查账号密码', 'error');
            }
        }).catch((err) => {
            this.showToast((err && err.msg) ? err.msg : '登录失败，请检查账号密码', 'error');
        })
    },

    /**
     * 是否已登录（同步判定函数，便于模板或逻辑中直接调用）
     */
    isLogin() {
        // 检查 localStorage 中的 token：存在且未过期才算已登录
        return isTokenValid();
    },

    /**
     * 登录守卫：所有「保存」「弹出编辑对话框」的按钮事件执行前先调用此函数。
     * - 已登录：返回 true，调用方继续执行原逻辑
     * - 未登录：弹出 loginModal，并返回 false，调用方应 return 中止原逻辑
     * 用法示例：
     *   if (!this.requireLogin()) return;
     *   // ...后续编辑/保存逻辑
     */
    requireLogin() {
        if (this.isLogin()) return true;
        this.openModal('loginModal');
        return false;
    },

    closeMobileNav() {
        const nav = this.$refs.navMenuItems;
        if (nav) {
            nav.classList.add('hidden');
            nav.classList.remove('flex');
        }
    },

    toggleMobileNav() {
        const nav = this.$refs.navMenuItems;
        if (nav) {
            nav.classList.toggle('hidden');
            nav.classList.toggle('flex');
        }
    },

    toggleTransferCalc() {
        if (!this.requireLogin()) return;
        const drawer = this.$refs.transferCalcDrawer;
        if (drawer) drawer.classList.toggle('hidden');
    },

    onDatePickerClick(e) {
        if (e.target.showPicker) {
            try { e.target.showPicker(); } catch (err) { /* noop */ }
        }
    },

    calculateAndSaveDueDate() {
        const calcTypeSelect = this.$refs.calcTypeSelect;
        const calcDateInput = this.$refs.calcDateInput;

        const calcType = calcTypeSelect ? calcTypeSelect.value : '';
        const calcDateVal = calcDateInput ? calcDateInput.value : '';

        if (!calcDateVal) {
            this.showToast("请选择日期！", 'error');
            return;
        }
        // calcType 为字典 id，从 pregnancyList 中取对应 dictName，解析"+ N天"得到推算天数
        const dict = this.pregnancyList.find(d => Number(d.id) === Number(calcType));
        const match = dict ? dict.dictName.match(/\+\s*(\d+)\s*天/) : null;
        const addDays = match ? Number(match[1]) : 280;

        const baseDate = new Date(calcDateVal);
        baseDate.setDate(baseDate.getDate() + addDays);
        const yyyy = baseDate.getFullYear();
        const mm = String(baseDate.getMonth() + 1).padStart(2, '0');
        const dd = String(baseDate.getDate()).padStart(2, '0');

        this.dueDate = `${yyyy}-${mm}-${dd}`;
        this.transferDate = calcDateVal;
        this.embryoType = calcType;

        // 保存移植日期与推算依据到用户资料（数据库）
        this.userInfo = {
            ...this.userInfo,
            yizhidate: calcDateVal,          // 选择移植 / 月经日期
            yuchanqituisuan: calcType        // 选择推算依据（字典 id）
        };
        indexjs.updateUserInfo(this.userInfo).then(res => {
            if (res && res.code === 200) {
                this.showToast('预产期设置已保存', 'success');
            } else {
                this.showToast((res && res.msg) || '保存失败！', 'error');
            }
        }).catch(err => {
            this.showToast((err && err.message) || '保存失败！', 'error');
        });

        this.updateCountdown();
        this.toggleTransferCalc();
    },

    updateCountdown() {
        const dueDate = new Date(this.dueDate);
        const today = new Date();
        dueDate.setHours(0, 0, 0, 0);
        const todayZero = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        const diffTime = dueDate - todayZero;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        const countdownEl = this.$refs.countdownDaysLarge;
        if (countdownEl) countdownEl.innerText = diffDays > 0 ? diffDays : 0;

        const dueDateTag = this.$refs.dueDateTag;
        if (dueDateTag) dueDateTag.innerText = this.dueDate;

        const totalDaysInPregnancy = 280;
        const elapsedDays = Math.max(0, totalDaysInPregnancy - diffDays);
        const currentWeeks = Math.floor(elapsedDays / 7);
        const currentDays = elapsedDays % 7;

        const gestationalWeekEl = this.$refs.gestationalWeek;
        if (gestationalWeekEl) {
            let trimester = "孕早期";
            if (currentWeeks >= 13 && currentWeeks < 28) trimester = "孕中期";
            if (currentWeeks >= 28) trimester = "孕晚期";
            gestationalWeekEl.innerText = `${trimester} · ${currentWeeks}周+${currentDays}天`;
        }

        const progressBar = this.$refs.gestationalProgressBar;
        if (progressBar) {
            const pct = Math.min(100, Math.max(0, Math.round((elapsedDays / totalDaysInPregnancy) * 100)));
            progressBar.style.width = `${pct}%`;
        }
    },

    switchTab(tabId) {
        this.closeMobileNav();
        document.querySelectorAll('.tab-content').forEach(el => el.classList.add('hidden'));
        document.querySelectorAll('.nav-item').forEach(btn => btn.classList.remove('active'));

        const targetContent = this.$refs[`tab-${tabId}`];
        if (targetContent) targetContent.classList.remove('hidden');

        const targetBtn = this.$refs[`nav-btn-${tabId}`];
        if (targetBtn) targetBtn.classList.add('active');

        const titles = {
            'tracker': { title: '⚡ 日常护理与生长速记中心', subtitle: '一键快速登记宝宝喂养、睡眠、换尿布、生长指标、皮肤护理、用药执行、疫苗与萌牙情况。' },
            'fetal': { title: '🤰 孕期胎儿数据与产检记录', subtitle: '记录孕期各周B超测量数据、胎心率及医生指导意见。' },
            'diary': { title: '📖 时光成长日记 (按年月归档)', subtitle: '按年月分组整理的微型卡片日记，支持本地照片视频与语音备忘。' },
            'album': { title: `🖼️ ${this.userInfo.chlidName || this.babyName}时光成长相册`, subtitle: `多维度沉淀${this.userInfo.chlidName || this.babyName}的照片与视频影像资料，支持按日期与标签智能筛选和全屏沉浸浏览。` },
            'guide': { title: '🥗 0-3岁科学喂养&4个月辅食计划', subtitle: '涵盖 0-1 岁奶量安排、4个月辅食菜谱计划表与 1-3 岁幼儿膳食指南。' },
            'attachment': { title: '❤️ 安全型依恋重塑与打卡', subtitle: '回避型奶爸的依恋重塑心理机制与 0-6 岁日常依恋互动打卡。' },
            'motor': { title: '🏃 0-3岁大运动与神经肌肉发育', subtitle: '头尾法则、肌肉骨骼发展与髓鞘化运动神经学机制。' },
            'brain': { title: '🧠 0-3岁大脑脑科学 (海马体/前额叶)', subtitle: '解密婴儿失忆症、恐惧警报与发脾气的脑神经发育图谱。' },
            'emotion': { title: '❤️ 0-10岁儿童情绪理解深度演进', subtitle: '结合发展心理学：为什么孩子要到10岁才能彻底理解矛盾/复杂情绪？' },
            'charts': { title: '📈 WHO 0-3岁生长曲线与评估', subtitle: '录入身高体重，对比世界卫生组织百分位标准。' },
            'mood': { title: '💭 奶爸心情微语与点滴随感', subtitle: '记录奶爸陪伴孩子成长中的喜怒哀乐、育儿感悟与时光片段，支持本地配图与短视频。' }
        };

        if (titles[tabId]) {
            this.$refs.currentTabTitle.innerHTML = titles[tabId].title;
            this.$refs.currentTabSubtitle.innerText = titles[tabId].subtitle;
        }

        if (tabId === 'charts') {
            // 加载WHO生长曲线与数据表记录 load_data 点击加载数据
            this.initGrowthCharts();
            this.updateGrowthSummary();
            this.renderGrowthRecords();
        } else if (tabId === 'fetal') {
            // 加载孕期胎儿数据与产检记录 load_data 点击加载数据
            this.renderFetalRecords();
        } else if (tabId === 'guide') {
            // 加载0-3岁科学喂养&4个月辅食计划 load_data 点击加载数据
            this.renderFoodPlan();
        } else if (tabId === 'diary') {
            // 加载时光成长日记 load_data 点击加载数据
            this.renderGroupedDiaries();
        } else if (tabId === 'album') {
            this.loadAlbumMediaList();
        } else if (tabId === 'mood') {
            // 加载心情微语 load_data 点击加载数据
            this.renderMoodPosts();
        }
    },

    // Settings Profile Logic
    handleAvatarUpload(e) {
        if (!this.requireLogin()) return;
        const file = e.target.files[0];
        if (!file) return;
        this.updateAvatarPreview(`上传中: ${file.name}`);
        // 上传到后端，只保存相对路径
        upload_image(file).then(data => {
            const rawUrl = data.url || data.fileName || data.filePath || '';
            this.avatarPath = rawUrl.replace(/^https?:\/\/[^/]+/i, '');
            this.avatarUrl = resolveMediaUrl(this.avatarPath);
            this.updateAvatarPreview(this.avatarUrl);
            this.showToast('头像上传成功', 'success');
        }).catch(err => {
            this.updateAvatarPreview(null);
            if (e.target) e.target.value = '';
            this.showToast((err && err.message) || '头像上传失败', 'error');
        });
    },

    resetAvatarToDefault() {
        this.avatarUrl = null;
        this.avatarPath = null;
        this.updateAvatarPreview(null);
    },

    updateAvatarPreview(url) {
        const preview = this.$refs.settingsAvatarPreview;
        if (!preview) return;
        if (url && /^https?:/.test(url)) {
            preview.innerHTML = `<img src="${url}" class="w-full h-full object-cover">`;
        } else if (url) {
            preview.innerHTML = `<span class="text-[10px] text-slate-500">${url}</span>`;
        } else {
            preview.innerHTML = `<i class="fa-solid fa-baby text-lg"></i>`;
        }
    },

    saveSiteSettings() {
        if (!this.requireLogin()) return;
        const siteTitleInput = this.$refs.settingsSiteTitleInput;
        const dadNameInput = this.$refs.settingsDadNameInput;
        const babyNameInput = this.$refs.settingsBabyNameInput;
        const dadStatusInput = this.$refs.settingsDadStatusInput;

        if (siteTitleInput && siteTitleInput.value.trim()) this.siteTitle = siteTitleInput.value.trim();
        if (dadNameInput && dadNameInput.value.trim()) this.dadName = dadNameInput.value.trim();
        if (babyNameInput && babyNameInput.value.trim()) this.babyName = babyNameInput.value.trim();
        if (dadStatusInput && dadStatusInput.value.trim()) this.dadStatus = dadStatusInput.value.trim();

        // 将表单值合并进 userInfo 后保存到数据库
        this.userInfo = {
            ...this.userInfo,
            webTitle: this.siteTitle,
            fatherName: this.dadName,
            chlidName: this.babyName,
            fatherTag: this.dadStatus,
            fatherImage: this.avatarPath || ''
        };

        indexjs.updateUserInfo(this.userInfo).then(res => {
            if (res && res.code === 200) {
                this.updateProfileUI();
                this.closeModal('siteSettingsModal');
                this.showToast('设置保存成功', 'success');
            } else {
                this.showToast((res && res.msg) || '保存失败！', 'error');
            }
        }).catch(err => {
            this.showToast((err && err.message) || '保存失败！', 'error');
        });
    },

    updateProfileUI() {
        const titleEl = this.$refs.sidebarSiteTitle;
        if (titleEl) titleEl.innerText = this.siteTitle;

        const statusEl = this.$refs.sidebarDadStatus;
        if (statusEl) statusEl.innerText = this.dadStatus;

        const avatarIcon = this.$refs.sidebarAvatarIcon;
        const avatarImg = this.$refs.sidebarAvatarImg;
        if (avatarIcon && avatarImg) {
            if (this.avatarUrl) {
                avatarIcon.classList.add('hidden');
                avatarImg.src = this.avatarUrl;
                avatarImg.classList.remove('hidden');
            } else {
                avatarImg.classList.add('hidden');
                avatarIcon.classList.remove('hidden');
            }
        }

        const moodAvatar = this.$refs.moodInputAvatar;
        if (moodAvatar) moodAvatar.innerText = this.dadName ? this.dadName.substring(0, 1) : '爸';

        // 同步设置弹窗表单与头像预览为当前值
        const siteTitleInput = this.$refs.settingsSiteTitleInput;
        if (siteTitleInput) siteTitleInput.value = this.siteTitle;
        const dadNameInput = this.$refs.settingsDadNameInput;
        if (dadNameInput) dadNameInput.value = this.dadName;
        const babyNameInput = this.$refs.settingsBabyNameInput;
        if (babyNameInput) babyNameInput.value = this.babyName;
        const dadStatusInput = this.$refs.settingsDadStatusInput;
        if (dadStatusInput) dadStatusInput.value = this.dadStatus;
        this.updateAvatarPreview(this.avatarUrl);

        this.renderMoodPosts();
    },

    quickLogFeed(paramMl) {
        if (!this.requireLogin()) return;
        const inputEl = this.$refs.customFeedMl;
        const ml = paramMl || (inputEl ? inputEl.value : '') || 90;
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "喂奶速记",
            content1: `配方奶`,
            content2: `${ml} ml`,
            content3: "喂奶速记",
            content4: "喂奶",
            createTime: timeStr,
            content5: "🍼"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (inputEl) inputEl.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    quickLogSleep(paramAction) {
        if (!this.requireLogin()) return;
        const selectEl = this.$refs.sleepTypeSelect;
        const action = paramAction || (selectEl ? selectEl.value : '开始入睡');
        const noteInput = this.$refs.sleepNoteInput;
        const note = noteInput && noteInput.value.trim() ? noteInput.value.trim() : "";
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "睡眠记录",
            content1: action,
            content2: note,
            content3: "睡眠记录",
            content4: "睡眠",
            createTime: timeStr,
            content5: "😴"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (noteInput) noteInput.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    quickLogDiaper(paramType) {
        if (!this.requireLogin()) return;
        const selectEl = this.$refs.diaperTypeSelect;
        const type = paramType || (selectEl ? selectEl.value : '仅尿湿');
        const diaperInput = this.$refs.diaperNoteInput;
        const note = diaperInput && diaperInput.value.trim() ? diaperInput.value.trim() : "";
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "换尿布",
            content1: type,
            content2: note,
            content3: "换尿布",
            content4: "换尿布",
            createTime: timeStr,
            content5: "💩"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (diaperInput) diaperInput.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    logGrowth() {
        if (!this.requireLogin()) return;
        const wInput = this.$refs.weightInput;
        const hInput = this.$refs.heightInput;
        const hcInput = this.$refs.headInput;

        const w = wInput ? wInput.value : '';
        const h = hInput ? hInput.value : '';
        const hc = hcInput ? hcInput.value : '';
        if (!w && !h && !hc) return;

        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "生长数据测定",
            content1: `${w||'-'}kg`,
            content2: `体重:${w||'-'}kg, 身高:${h||'-'}cm, 头围:${hc||'-'}cm`,
            content3: "生长数据测定",
            content4: "生长",
            createTime: timeStr,
            content5: "📏"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (wInput) wInput.value = '';
                if (hInput) hInput.value = '';
                if (hcInput) hcInput.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    // 皮肤护理与洗澡记录
    quickLogSkin(paramAction) {
        if (!this.requireLogin()) return;
        const selectEl = this.$refs.skinTypeSelect;
        const action = paramAction || (selectEl ? selectEl.value : '洗澡与抚触');
        const skinInput = this.$refs.skinNoteInput;
        const note = skinInput && skinInput.value.trim() ? skinInput.value.trim() : "";
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "皮肤护理与洗澡",
            content1: action,
            content2: note,
            content3: "皮肤护理与洗澡",
            content4: "护肤",
            createTime: timeStr,
            content5: "🧴"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (skinInput) skinInput.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    quickLogMed(paramMedName) {
        if (!this.requireLogin()) return;
        const selectEl = this.$refs.medSelect;
        const customInput = this.$refs.customMedInput;
        let medName = paramMedName;
        if (!medName) {
            if (customInput && customInput.value.trim()) {
                medName = customInput.value.trim();
            } else if (selectEl) {
                medName = selectEl.value;
            }
        }
        if (!medName || medName === '其他') return;
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "用药打卡",
            content1: selectEl ? selectEl.value : (paramMedName || ''),
            content2: customInput ? customInput.value.trim() : '',
            content3: "用药打卡",
            content4: "用药",
            createTime: timeStr,
            content5: "💊"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (customInput) customInput.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    // 疫苗接种记录
    quickLogVaccine() {
        if (!this.requireLogin()) return;
        const vacSelect = this.$refs.vaccineSelect;
        const vac = vacSelect ? vacSelect.value : '注射：乙肝疫苗第一针';
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "疫苗接种记录",
            content1: vac,
            content2: vac,
            content3: "疫苗接种",
            content4: "疫苗",
            createTime: timeStr,
            content5: "💉"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    // 出牙记录
    quickLogTeeth() {
        if (!this.requireLogin()) return;
        const toothSelect = this.$refs.teethPositionSelect;
        const tooth = toothSelect ? toothSelect.value : '下门牙左/右';
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "出牙记录",
            content1: tooth,
            content2: tooth,
            content3: "出牙记录",
            content4: "萌牙",
            createTime: timeStr,
            content5: "🦷"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    // 自定义速记事项
    quickLogCustomEvent() {
        if (!this.requireLogin()) return;
        const nameInput = this.$refs.customEventName;
        const noteInput = this.$refs.customEventNote;
        const name = nameInput ? nameInput.value.trim() : '';
        const note = noteInput ? noteInput.value.trim() : '';
        if (!name) return;

        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "自定义速记事项",
            content1: name,
            content2: note,
            content3: "自定义速记事项",
            content4: "其他",
            createTime: timeStr,
            content5: "📝"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (nameInput) nameInput.value = '';
                if (noteInput) noteInput.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    // 安全依恋亲子互动打卡
    quickLogAttachment() {
        if (!this.requireLogin()) return;
        const attachSelect = this.$refs.attachmentInteractionSelect;
        const act = attachSelect ? attachSelect.value : '完成15分钟皮肤接触';
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "安全依恋亲子互动打卡",
            content1: act,
            content2: act,
            content3: "依恋重塑",
            content4: "依恋",
            createTime: timeStr,
            content5: "❤️"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    filterTrackerLogs(category) {
        this.renderTrackerLogs(category);
    },

    clearTrackerLogs() {
        this.trackerLogs = [];
        this.renderTrackerLogs('ALL');
    },

    renderTrackerLogs(filterCategory = 'ALL') {
        const container = this.$refs.trackerHistoryList;
        if (!container) return;

        // 点击“全部显示”：重新向后端查询，用 maxSize 一次性把全部数据拉回来
        if (filterCategory === 'ALL') {
            indexjs.getDailyList({ 'pageNum': 1, 'pageSize': this.maxSize })
                .then(res => {
                    if (res && res.code === 200) {
                        this.trackerLogs = res.rows || (res.data && res.data.rows) || res.data || [];
                        this.drawTrackerLogs(this.trackerLogs);
                    }
                })
                .catch(err => {
                    console.error('查询日常护理记录失败:', err);
                });
            return;
        }

        // 指定分类：基于已加载的数据在前端过滤
        const filtered = this.trackerLogs.filter(item => item.tag === filterCategory || item.category === filterCategory);
        this.drawTrackerLogs(filtered);
    },

    // 纯渲染：把传入的记录列表绘制到打卡容器
    drawTrackerLogs(list) {
        const container = this.$refs.trackerHistoryList;
        if (!container) return;

        if (!list || list.length === 0) {
            container.innerHTML = `<div class="text-center text-xs text-slate-400 py-6">暂无此类打卡记录。</div>`;
            return;
        }

        container.innerHTML = list.map(log => `
            <div class="bg-slate-50 border border-slate-200 rounded-xl p-3 flex justify-between items-center text-xs hover:bg-sky-50/50 transition">
                <div class="flex items-center space-x-3">
                    <span class="text-lg">${log.content5 || '📝'}</span>
                    <div>
                        <div class="flex items-center space-x-2">
                            <span class="font-bold text-slate-800">${log.typestr}</span>
                            <span class="bg-sky-100 text-sky-800 text-[10px] px-2 py-0.2 rounded font-semibold">${log.content1}</span>
                        </div>
                        <p class="text-slate-500 text-[11px] mt-0.5 txtleft">${log.content2}</p>
                    </div>
                </div>
                <div class="flex items-center space-x-2">
                    <span class="text-slate-400 font-mono text-[10px]">${log.createTime}</span>
                    <button data-action="deleteTrackerLog" data-id="${log.id}" class="text-slate-400 hover:text-rose-600 text-xs transition" title="删除此条记录">
                        <i class="fa-solid fa-trash-can"></i>
                    </button>
                </div>
            </div>
        `).join('');
    },

    // 从数据库刷新日常护理打卡列表：默认只显示前 10 条
    refreshTrackerLogs() {
        const container = this.$refs.trackerHistoryList;
        if (!container) return Promise.resolve();
        return indexjs.getDailyList({ 'pageNum': 1, 'pageSize': 10 })
            .then(res => {
                if (res && res.code === 200) {
                    this.trackerLogs = res.rows || (res.data && res.data.rows) || res.data || [];
                    this.drawTrackerLogs(this.trackerLogs);
                }
            })
            .catch(err => {
                console.error('查询日常护理记录失败:', err);
            });
    },

    deleteTrackerLog(id) {
        if (!this.requireLogin()) return;
        if (!id) return;
        indexjs.deleteDaily(id).then(res => {
            if (res && res.code === 200) {
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('删除日常护理记录失败:', err);
        })
    },

    handleMoodFileUpload(e) {
        if (!this.requireLogin()) return;
        const file = e.target.files[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        const type = file.type.startsWith('video/') ? 'video' : 'image';
        this.uploadedMoodFile = { url, type, name: file.name, file };

        const txt = this.$refs.moodFilePreviewText;
        if (txt) {
            txt.innerText = `📎 ${file.name.substring(0, 10)}...`;
            txt.classList.remove('hidden');
        }
    },

    saveMoodPost() {
        if (!this.requireLogin()) return;
        const moodTextInput = this.$refs.moodTextInput;
        const moodTagSelect = this.$refs.moodTagSelect;

        const text = moodTextInput ? moodTextInput.value.trim() : '';

        if (!text) {
            // 兜底：正常情况下表单 required 已由浏览器在输入框下方弹出提示
            const form = this.$refs.moodPostForm;
            if (form && form.reportValidity) form.reportValidity();
            return;
        }
        const tag = moodTagSelect ? moodTagSelect.value : '';

        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;

        // 构造后台 Comment 实体
        const item = {
            content: text,
            typeid: tag,
            files: '',
            createTime: timeStr,
            // likes: 0,
        };

        // 提交到数据库，成功后清空输入并刷新列表
        const submit = () => {
            indexjs.addComment(item).then(res => {
                if (res && res.code === 200) {
                    this.showToast("心情微语已发布！", 'success');
                    this.uploadedMoodFile = null;
                    if (moodTextInput) moodTextInput.value = '';
                    const txt = this.$refs.moodFilePreviewText;
                    if (txt) txt.classList.add('hidden');
                    const fileInput = this.$refs.moodFileInput;
                    if (fileInput) fileInput.value = '';
                    this.renderMoodPosts();
                } else {
                    this.showToast(res.msg || "发布失败！", 'error');
                }
            }).catch(err => {
                console.error('添加心情微语失败:', err);
                this.showToast(err.message || '发布失败！', 'error');
            });
        };

        if (this.uploadedMoodFile && this.uploadedMoodFile.file) {
            // 先把配图/视频上传到服务器，只保存相对地址
            upload_image(this.uploadedMoodFile.file).then(data => {
                const rawUrl = data.url || data.fileName || data.filePath || '';
                item.files = rawUrl.replace(/^https?:\/\/[^/]+/i, '');
                submit();
            }).catch(err => {
                console.error('上传心情配图失败:', err);
                this.showToast('媒体上传失败！', 'error');
            });
        } else {
            submit();
        }
    },

    toggleLikeMood(id) {
        const post = this.moodPosts.find(p => p.id === id);
        if (!post) return;

        // 本地 +1，只更新对应按钮的显示，不刷新整个列表
        post.likes = (post.likes || 0) + 1;
        const container = this.$refs.moodFeedContainer;
        const btn = container ? container.querySelector(`[data-action="toggleLikeMood"][data-id="${id}"]`) : null;
        if (btn) {
            const icon = btn.querySelector('i');
            const span = btn.querySelector('span');
            if (icon) icon.classList.add('text-rose-500');
            if (span) span.innerText = `给爸爸加油 (${post.likes})`;
        }

        // 数据库正常更新
        indexjs.updateComment({'id': id, 'likes': post.likes}).then(res => {
            if (!res || res.code !== 200) {
                this.showToast((res && res.msg) || '点赞失败！', 'error');
            }
        }).catch(err => {
            console.error('点赞失败:', err);
            this.showToast(err.message || '点赞失败！', 'error');
        });
    },

    deleteMoodPost(id) {
        if (!this.requireLogin()) return;
        if (!id) return;
        indexjs.deleteComment(id).then(res => {
            if (res && res.code === 200) {
                this.showToast("心情微语已删除！", 'success');
                this.renderMoodPosts();
            } else {
                this.showToast((res && res.msg) || '删除失败！', 'error');
            }
        }).catch(err => {
            console.error('删除心情微语失败:', err);
            this.showToast(err.message || '删除失败！', 'error');
        });

        this.moodPosts = this.moodPosts.filter(p => p.id !== id);
        this.renderMoodPosts();
    },

    renderMoodPosts() {
        const container = this.$refs.moodFeedContainer;
        if (!container) return;

        const doRender = () => {
            if (this.moodPosts.length === 0) {
                container.innerHTML = `<div class="text-center text-xs text-slate-400 py-10">暂无心情微语，快发第一条心情吧！</div>`;
                return;
            }

            const dadName = this.dadName || "独爱秉宣的奶爸";

            container.innerHTML = this.moodPosts.map(p => `
                <div class="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-2xs space-y-3 hover:border-amber-300 transition">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center space-x-3">
                            <div class="w-10 h-10 rounded-full bg-gradient-to-tr from-amber-400 to-orange-500 text-white font-extrabold flex items-center justify-center text-sm shadow-xs overflow-hidden">
                                ${this.avatarUrl ? `<img src="${this.avatarUrl}" class="w-full h-full object-cover">` : dadName.substring(0, 1)}
                            </div>
                            <div>
                                <div class="flex items-center space-x-2">
                                    <span class="font-bold text-xs sm:text-sm text-slate-800">${dadName}</span>
                                    <span class="bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded-full font-bold">${p.tag}</span>
                                </div>
                                <span class="text-[10px] text-slate-400 font-mono">${p.time}</span>
                            </div>
                        </div>
                        <button data-action="deleteMoodPost" data-id="${p.id}" class="text-slate-400 hover:text-rose-500 text-xs transition p-1" title="删除该心情">
                            <i class="fa-solid fa-trash-can"></i>
                        </button>
                    </div>

                    ${p.text ? `<p class="text-xs sm:text-sm text-slate-700 whitespace-pre-line leading-relaxed font-sans">${p.text}</p>` : ''}

                    ${p.media ? `
                        <div class="rounded-xl overflow-hidden bg-slate-900 border border-slate-200 max-h-72 max-w-md">
                            ${p.mediaType === 'video' ? `
                                <video controls src="${p.media}" class="max-h-72 w-full object-contain"></video>
                            ` : `
                                <img src="${p.media}" alt="Mood media" class="max-h-72 w-full object-cover">
                            `}
                        </div>
                    ` : ''}

                    <div class="flex justify-between items-center pt-2 border-t border-slate-100 text-xs">
                        <button data-action="toggleLikeMood" data-id="${p.id}" class="text-slate-500 hover:text-rose-500 font-medium transition flex items-center space-x-1.5">
                            <i class="fa-solid fa-heart ${p.likes > 0 ? 'text-rose-500' : 'text-slate-400'}"></i>
                            <span>给爸爸加油 (${p.likes || 0})</span>
                        </button>
                        <span class="text-[10px] text-slate-400">来自 ${this.siteTitle}</span>
                    </div>
                </div>
            `).join('');
        };

        indexjs.getCommentList({ 'pageNum': 1, 'pageSize': this.maxSize }).then(res => {
            if (res && res.code === 200) {
                const rows = res.rows || (res.data && res.data.rows) || res.data || [];
                // 将后台 Comment 数据转换为前端 moodPosts 格式
                this.moodPosts = rows.map(r => {
                    const media = r.files ? resolveMediaUrl(r.files) : null;
                    const isVideo = media && /\.(mp4|mov|webm|avi|m4v)$/i.test(media);
                    // typeid 存的是字典 id，从 moodList 中反查 dictName 用于展示
                    const tagName = getDictValuesByField(JSON.stringify(this.moodList.filter(m => Number(m.id) === Number(r.typeid))), 'dictName', ' ');
                    return {
                        id: r.id,
                        text: r.content,
                        tag: tagName || r.typeid,
                        time: r.createTime,
                        media: media,
                        mediaType: media ? (isVideo ? 'video' : 'image') : null,
                        likes: r.likes || 0
                    };
                });
            }
            doRender();
        }).catch(err => {
            console.error('查询心情微语失败:', err);
            doRender();
        });
    },

    // Fetal Records
    saveFetalCheckup(e) {
        if (!this.requireLogin()) return;
        e.preventDefault();
        const checkDate = this.$refs.fetalDate.value;
        const week = this.$refs.fetalWeek.value;
        const bpm = this.$refs.fetalHeartRate.value;
        const weight = this.$refs.fetalWeight.value;
        const bpd = this.$refs.fetalBpd.value;
        const fl = this.$refs.fetalFl.value;
        const ac = this.$refs.fetalAc.value;
        const afd = this.$refs.fetalAmniotic.value;
        const tip = this.$refs.fetalDoctorSummary.value;

        let item = {
            'checkDate':checkDate, 
            'week':week,
            'bpm':bpm,
            'weight':weight,
            'bpd':bpd,
            'fl':fl,
            'ac':ac,
            'afd':afd,
            'tip':tip,
        }
        indexjs.addPregnancy(item).then(res => {
            if (res.code === 200) {
                this.renderFetalRecords();
                this.$refs.fetalDataForm.reset();
            }
        });
    },

    deleteFetalRecord(id) {
        if (!this.requireLogin()) return;

        if(id>0){
            indexjs.deletePregnancy(id).then(res => {
                if (res.code === 200) {
                    this.renderFetalRecords();
                }
            });
        }


        this.fetalRecords.filter(r => r.id !== id);
        this.renderFetalRecords();
    },

    // 加载孕期胎儿数据与产检记录：从数据库查询后渲染
    renderFetalRecords() {
        const container = this.$refs.fetalHistoryContainer;
        if (!container) return;

        indexjs.getPregnancyList({
            'pageNum': 1,
            'pageSize': this.maxSize
        }).then(res => {
            if (res && res.code === 200) {
                this.fetalRecords = res.rows || (res.data && res.data.rows) || res.data || [];
            }
            this.drawFetalRecords();
        }).catch(err => {
            console.error('查询产检记录失败:', err);
            this.drawFetalRecords();
        });
    },

    // 纯渲染：把当前 fetalRecords 绘制到产检档案容器
    drawFetalRecords() {
        const container = this.$refs.fetalHistoryContainer;
        if (!container) return;

        if (this.fetalRecords.length === 0) {
            container.innerHTML = `<div class="text-center text-xs text-slate-400 py-6">暂无产检数据记录。</div>`;
            return;
        }

        container.innerHTML = this.fetalRecords.map(r => `
            <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-2">
                <div class="flex justify-between items-center border-b border-slate-100 pb-2">
                    <div class="flex items-center space-x-2">
                        <span class="font-extrabold text-xs sm:text-sm text-slate-800">${r.week}</span>
                        <span class="text-[10px] bg-rose-100 text-rose-800 px-2 py-0.5 rounded font-mono">${r.checkDate}</span>
                    </div>
                    <button data-action="deleteFetalRecord" data-id="${r.id}" class="text-slate-400 hover:text-rose-600 text-xs transition">
                        <i class="fa-solid fa-trash-can"></i>
                    </button>
                </div>

                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-slate-600 font-mono bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                    <div>💓 胎心率: <strong class="text-slate-800">${r.bpm || '-'} bpm</strong></div>
                    <div>⚖️ 估重: <strong class="text-slate-800">${r.weight || '-'} g</strong></div>
                    <div>📏 双顶径: <strong class="text-slate-800">${r.bpd || '-'} mm</strong></div>
                    <div>🦴 股骨长: <strong class="text-slate-800">${r.fl || '-'} mm</strong></div>
                    <div> 腹围: <strong class="text-slate-800">${r.ac || '-'} mm</strong></div>
                    <div> 羊水最大深度: <strong class="text-slate-800">${r.afd || '-'} mm</strong></div>
                </div>

                <div class="text-xs text-slate-700 leading-relaxed bg-amber-50/50 p-2.5 rounded-lg border border-amber-100">
                    <span class="font-bold text-amber-900">👨‍⚕️ 医生指导与总结：</span>
                    <p class="mt-0.5 text-slate-700">${r.tip}</p>
                </div>
            </div>
        `).join('');
    },

    // Diaries Logic
    applyWolaiTemplate(type) {
        if (!this.requireLogin()) return;
        const titleInput = this.$refs.diaryTitleInput;
        const contentInput = this.$refs.diaryContentInput;
        const categoryInput = this.$refs.diaryCategoryInput;

        if (type === 'daily') {
            if (titleInput) titleInput.value = `王秉宣育儿日常复盘 - ${new Date().toLocaleDateString()}`;
            if (categoryInput) categoryInput.value = 62; // '日常';
            if (contentInput) contentInput.value = `【今日护理核心总结】\n- 喂奶次数/奶量：\n- 换尿布/便便性状：\n- 睡眠时长与质量：\n\n【大运动与互动情况】\n- 完成了15分钟趴卧练习与婴儿抚触。\n\n【奶爸感悟与心得】\n- 应对哭闹时保持了平静，及时给予回应。`;
        } else if (type === 'checkup') {
            if (titleInput) titleInput.value = `孕期产检B超纪录 - 孕周复盘`;
            if (categoryInput) categoryInput.value = 63; // '孕期';
            if (contentInput) contentInput.value = `【产检概况】\n- 检查日期：\n- 当前孕周：\n- 胎儿估重/胎心率：\n\n【医生嘱咐】\n- 补钙与蛋白摄入要求：\n\n【奶爸备产心里话】\n- 听到宝宝心跳那一刻，心里充满了踏实与期待。`;
        } else if (type === 'milestone') {
            if (titleInput) titleInput.value = `🏆 第一次里程碑突破：秉宣学会了...`;
            if (categoryInput) categoryInput.value = 64; // '里程碑';
            if (contentInput) contentInput.value = `【里程碑时刻】\n- 时间：\n- 突破动作/技能：（如第一次翻身 / 第一次发音“打打”）\n\n【细节与陪伴过程】\n- 爸爸全程陪在身边，及时给予了积极掌声与鼓舞！`;
        } else if (type === 'voice') {
            if (titleInput) titleInput.value = `🎙️ 奶爸声音留声机 - 留给秉宣的话`;
            if (categoryInput) categoryInput.value = 66; // '语音备忘';
            if (contentInput) contentInput.value = `宣宣，这是爸爸给你留下的声音档案...\n愿你健康快乐成长，爸爸永远是你的坚实后盾！`;
        }
    },

    handleLocalFileUpload(e) {
        if (!this.requireLogin()) return;
        const file = e.target.files[0];
        if (!file) return;
        let type = 'image';
        if (file.type.startsWith('video/')) type = 'video';
        if (file.type.startsWith('audio/')) type = 'audio';

        const previewBadge = this.$refs.localFilePreviewBadge;
        const fileNameText = this.$refs.localFileNameText;
        const fileTypeTag = this.$refs.localFileTypeTag;

        // 先展示选中状态，并提示上传中
        if (previewBadge && fileNameText && fileTypeTag) {
            fileNameText.innerText = `上传中: ${file.name}`;
            fileTypeTag.innerText = type === 'image' ? '照片' : (type === 'video' ? '视频' : '语音');
            previewBadge.classList.remove('hidden');
        }

        // 调用网页端上传实现，把文件传到后端
        upload_image(file).then(data => {
            // 后端返回的 data 中通常含 url / name / fileName / filePath 等字段
            // 只保存相对地址（如 /profile/upload/2026/08/31/xxx.png），IP/域名展示时统一拼接 BASE_URL
            const rawUrl = data.url || data.fileName || data.filePath || '';
            const url = rawUrl.replace(/^https?:\/\/[^/]+/i, '');
            this.uploadedLocalFile = { url, type, name: file.name };

            // 把服务器地址回填到媒体链接输入框，saveNewDiary 提交时即可带上
            // const mediaInput = this.$refs.diaryMediaInput;
            // if (mediaInput) mediaInput.value = url;

            if (fileNameText) fileNameText.innerText = `${file.name} (已上传)`;
            this.showToast('文件上传成功', 'success');
        }).catch(err => {
            // 上传失败：清空选择，恢复提示
            if (fileNameText) fileNameText.innerText = `${file.name} (上传失败)`;
            this.uploadedLocalFile = null;
            const input = this.$refs.diaryLocalFileInput;
            if (input) input.value = '';
            this.showToast((err && err.message) || '文件上传失败', 'error');
        });
    },

    simulateVoiceRecord() {
        if (!this.requireLogin()) return;
        // 已在录音中：停止录音
        if (this.isRecording) {
            if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
                this.mediaRecorder.stop();
            }
            this.isRecording = false;
            return;
        }
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof MediaRecorder === 'undefined') {
            this.showToast('当前浏览器不支持录音功能', 'error');
            return;
        }
        // 后端上传仅支持 mp4，浏览器必须支持录制 audio/mp4，否则不录音也不上传
        const mimeType = ['audio/mp4', 'audio/mp4;codecs=mp4a.40.2'].find(m => MediaRecorder.isTypeSupported(m)) || '';
        if (!mimeType) {
            this.showToast('当前浏览器不支持录制 MP4 格式，无法使用录音功能', 'error');
            return;
        }
        navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
            const chunks = [];
            const recorder = new MediaRecorder(stream, { mimeType });
            recorder.ondataavailable = e => {
                if (e.data && e.data.size > 0) chunks.push(e.data);
            };
            recorder.onstop = () => {
                stream.getTracks().forEach(t => t.stop());
                const finalMime = recorder.mimeType || mimeType;
                const ext = finalMime.includes('mp4') ? 'mp4' : 'webm';
                const blob = new Blob(chunks, { type: finalMime });

                // 计算并回填语音时长 mm:ss
                const seconds = Math.round((Date.now() - this.recordStartTs) / 1000);
                const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
                const ss = String(seconds % 60).padStart(2, '0');
                const audioInput = this.$refs.diaryAudioInput;
                if (audioInput) audioInput.value = `${mm}:${ss}`;

                // 上传录音到后端，只保存相对路径
                const fileName = `voice_${Date.now()}.${ext}`;
                const file = new File([blob], fileName, { type: finalMime });
                const previewBadge = this.$refs.localFilePreviewBadge;
                const fileNameText = this.$refs.localFileNameText;
                const fileTypeTag = this.$refs.localFileTypeTag;
                if (previewBadge && fileNameText && fileTypeTag) {
                    fileNameText.innerText = `上传中: ${fileName}`;
                    fileTypeTag.innerText = '语音';
                    previewBadge.classList.remove('hidden');
                }
                upload_image(file).then(data => {
                    const rawUrl = data.url || data.fileName || data.filePath || '';
                    const url = rawUrl.replace(/^https?:\/\/[^/]+/i, '');
                    this.uploadedLocalFile = { url, type: 'audio', name: fileName };
                    if (fileNameText) fileNameText.innerText = `${fileName} (已上传)`;
                    this.showToast(`录音完成（${mm}:${ss}）`, 'success');
                }).catch(err => {
                    if (fileNameText) fileNameText.innerText = `${fileName} (上传失败)`;
                    this.uploadedLocalFile = null;
                    this.showToast((err && err.message) || '录音上传失败', 'error');
                });
            };
            recorder.start();
            this.mediaRecorder = recorder;
            this.recordStartTs = Date.now();
            this.isRecording = true;
            this.showToast('录音中，再次点击"停止"结束', 'info');
        }).catch(err => {
            this.showToast('无法访问麦克风：' + ((err && err.message) || '请检查录音权限'), 'error');
        });
    },

    saveNewDiary() {
        if (!this.requireLogin()) return;
        const titleInput = this.$refs.diaryTitleInput;
        const categoryInput = this.$refs.diaryCategoryInput;
        const dateInput = this.$refs.diaryDateInput;
        const contentInput = this.$refs.diaryContentInput;
        const mediaInput = this.$refs.diaryMediaInput;
        const audioFileInput = this.uploadedLocalFile;

        const title = titleInput ? titleInput.value.trim() : '';
        const typeid = categoryInput.value;
        const noteDate = (dateInput && dateInput.value) ? dateInput.value : new Date().toISOString().split('T')[0];
        const content = contentInput ? contentInput.value.trim() : '';
        const mediaUrl = mediaInput ? mediaInput.value.trim() : '';
        const audioFile = audioFileInput ? audioFileInput.url : null; // 图片/短视频/录音附件

        if (!title || !content) {
            // 兜底：正常情况下表单 required 已由浏览器在输入框下方弹出提示
            const form = this.$refs.diaryEntryForm;
            if (form && form.reportValidity) form.reportValidity();
            return;
        }

        let item = {
            'title': title, // 标题
            'typeid': typeid, // 分类标签
            'noteDate': noteDate, // 记录日期
            'content': content, // 内容
             'mediaUrl': mediaUrl, // 网络媒体链接 (可选)
             'files': audioFile, // 图片/短视频/录音附件 (可选)
            // audioTime, // 语音时长
            // audioFile, // 语音文件
        }

        indexjs.addNote(item).then(res => {
            if (res.code === 200) {
                this.showToast("添加成功！", 'success');
                this.renderGroupedDiaries();
            } else {
                this.showToast(res.msg || "添加失败！", 'error');
            }
        })

        this.closeModal('quickAddModal');
        this.uploadedLocalFile = null;
        if (titleInput) titleInput.value = '';
        if (contentInput) contentInput.value = '';
        if (mediaInput) mediaInput.value = '';
        const badge = this.$refs.localFilePreviewBadge;
        if (badge) badge.classList.add('hidden');

        this.renderGroupedDiaries();
    },

    filterDiary(cat) {
        this.currentDiaryCategory = cat;
        this.renderGroupedDiaries();
    },

    // 统计某个分类（typeid）下的日记数量
    diaryCountByType(typeid) {
        return this.diaries.filter(d => Number(d.typeid) === Number(typeid)).length;
    },

    // 查询日记列表，转换格式后渲染分组日记
    renderGroupedDiaries() {
        const container = this.$refs.groupedDiariesContainer;
        if (!container) return;

        indexjs.getNoteList({'pageNum': 1, 'pageSize': this.maxSize}).then(res => {
            if (res && res.code === 200) {
                // rows 每行转换为 diaries 展示格式
                this.diaries = (res.rows || []).map(r => {
                    // 根据 typeid 从 diaryList 字典中取分类名称
                    const dict = this.diaryList.find(d => d.id === r.typeid);
                    const date = r.noteDate || '';
                    let yearMonth = '';
                    if (date) {
                        const [y, m] = date.split('-');
                        yearMonth = `${y}年${Number(m)}月`;
                    }
                    return {
                        id: r.id,
                        title: r.title,
                        category: dict ? dict.dictName : '',
                        typeid: r.typeid,
                        date: date,
                        yearMonth: yearMonth,
                        content: r.content,
                        media: resolveMediaUrl(r.files || r.mediaUrl), // 相对地址拼接 BASE_URL
                        mediaType: (() => {
                            // 根据文件后缀判断媒体类型：video / audio / image
                            const u = (r.files || r.mediaUrl || '');
                            if (!u) return null;
                            if (/\.(mp4|mov|webm|avi)(\?.*)?$/i.test(u)) return 'video';
                            if (/\.(mp3|wav|m4a|aac|ogg)(\?.*)?$/i.test(u)) return 'audio';
                            return 'image';
                        })(),
                        audioDuration: r.audioTime
                    };
                });
            }
            this.drawGroupedDiaries();
        }).catch(err => {
            console.error('获取日记列表失败:', err);
            this.drawGroupedDiaries();
        });
    },

    // 纯渲染：按分类过滤并分组绘制日记卡片
    drawGroupedDiaries() {
        const container = this.$refs.groupedDiariesContainer;
        if (!container) return;

        let filtered = this.diaries;
        if (this.currentDiaryCategory !== 'ALL') {
            filtered = this.diaries.filter(d => Number(d.typeid) === Number(this.currentDiaryCategory));
        }

        const countEl = this.$refs.diaryCountAll;
        if (countEl) countEl.innerText = this.diaries.length;

        if (filtered.length === 0) {
            container.innerHTML = `<div class="text-center text-xs text-slate-400 py-10">暂无日记记录。</div>`;
            return;
        }

        const groups = {};
        filtered.forEach(item => {
            if (!groups[item.yearMonth]) groups[item.yearMonth] = [];
            groups[item.yearMonth].push(item);
        });

        container.innerHTML = Object.keys(groups).map(ym => `
            <div class="space-y-3">
                <div class="flex items-center space-x-2 border-b border-slate-200 pb-1.5">
                    <i class="fa-solid fa-calendar-days text-indigo-500 text-xs"></i>
                    <h4 class="font-extrabold text-xs text-slate-800 font-mono">${ym}</h4>
                    <span class="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.2 rounded-full font-bold">${groups[ym].length} 篇</span>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    ${groups[ym].map(d => `
                        <div data-action="openDiaryDetail" data-id="${d.id}" class="bg-white border border-slate-200 hover:border-indigo-400 rounded-xl p-3.5 shadow-2xs cursor-pointer hover:shadow-md transition flex flex-col justify-between space-y-2 h-36 group">
                            <div class="space-y-1">
                                <div class="flex justify-between items-center text-[10px]">
                                    <span class="bg-sky-100 text-sky-800 px-2 py-0.5 rounded font-bold">${d.category}</span>
                                    <span class="text-slate-400 font-mono">${d.date}</span>
                                </div>
                                <h5 class="font-bold text-xs text-slate-800 group-hover:text-indigo-600 transition line-clamp-2 leading-snug">${d.title}</h5>
                                <p class="text-[11px] text-slate-500 line-clamp-2 leading-tight">${d.content}</p>
                            </div>

                            <div class="flex items-center justify-between text-[10px] text-slate-400 border-t border-slate-100 pt-1.5">
                                <span class="flex items-center gap-1">
                                    ${d.media ? '<i class="fa-solid fa-paperclip text-indigo-500"></i>' : ''}
                                    ${d.audioDuration ? '<i class="fa-solid fa-microphone text-purple-500"></i>' : ''}
                                    <span>查看全文</span>
                                </span>
                                <i class="fa-solid fa-chevron-right text-[8px] group-hover:translate-x-0.5 transition"></i>
                            </div>
                        </div>
                    `).join('')}
                </div>
            </div>
        `).join('');
    },

    openDiaryDetail(id) {
        const item = this.diaries.find(d => d.id === id);
        if (!item) return;

        this.$refs.detailTitle.innerText = item.title;
        this.$refs.detailCategoryTag.innerText = item.category;
        this.$refs.detailDate.innerText = item.date;
        this.$refs.detailContent.innerText = item.content;

        const mediaBox = this.$refs.detailMediaBox;
        const imgEl = this.$refs.detailImg;
        const videoEl = this.$refs.detailVideo;

        if (item.media && item.mediaType !== 'audio') {
            mediaBox.classList.remove('hidden');
            if (item.mediaType === 'video') {
                imgEl.classList.add('hidden');
                videoEl.src = item.media;
                videoEl.classList.remove('hidden');
            } else {
                videoEl.classList.add('hidden');
                imgEl.src = item.media;
                imgEl.classList.remove('hidden');
            }
        } else {
            mediaBox.classList.add('hidden');
        }

        const audioBox = this.$refs.detailAudioBox;
        const audioPlayer = this.$refs.detailAudioPlayer;
        if (item.mediaType === 'audio' && item.media) {
            // 上传的音频附件：直接用原生播放器播放
            audioBox.classList.remove('hidden');
            if (audioPlayer) {
                audioPlayer.src = item.media;
                audioPlayer.classList.remove('hidden');
            }
            this.$refs.detailAudioDuration.innerText = item.audioDuration || '音频附件';
        } else if (item.audioDuration) {
            audioBox.classList.remove('hidden');
            if (audioPlayer) audioPlayer.classList.add('hidden');
            this.$refs.detailAudioDuration.innerText = item.audioDuration;
        } else {
            audioBox.classList.add('hidden');
        }

        this.$refs.deleteDetailBtn.onclick = () => {
            this.deleteDiary(id);
            this.closeModal('diaryDetailModal');
        };

        this.openModal('diaryDetailModal');
    },

    deleteDiary(id) {
        if (!this.requireLogin()) return;

        indexjs.deleteNote(id).then(() => {
            this.showToast("删除成功！", 'success');
            this.diaries = this.diaries.filter(d => d.id !== id);
            this.renderGroupedDiaries();
        })
        
    },

    playDetailVoice() {
        this.showToast("正在播放爸爸的声音留声机音频...", 'info');
    },

    handleAlbumLocalFileUpload(e) {
        if (!this.requireLogin()) return;
        const file = e.target.files[0];
        if (!file) return;
        const type = file.type.startsWith('video/') ? 'video' : 'image';

        const badge = this.$refs.albumLocalFileBadge;
        const nameTxt = this.$refs.albumLocalFileNameText;
        const typeTag = this.$refs.albumLocalFileTypeTag;

        // 先展示选中状态，并提示上传中
        if (nameTxt) nameTxt.innerText = `上传中: ${file.name}`;
        if (typeTag) typeTag.innerText = type === 'image' ? '照片' : '视频';
        if (badge) badge.classList.remove('hidden');

        // 上传到后端，只保存相对地址（如 /profile/upload/2026/08/31/xxx.png）
        upload_image(file).then(data => {
            const rawUrl = data.url || data.fileName || data.filePath || '';
            const url = rawUrl.replace(/^https?:\/\/[^/]+/i, '');
            this.uploadedAlbumLocalFile = { url, type, name: file.name };

            if (nameTxt) nameTxt.innerText = `${file.name} (已上传)`;
            this.showToast('文件上传成功', 'success');
        }).catch(err => {
            if (nameTxt) nameTxt.innerText = `${file.name} (上传失败)`;
            this.uploadedAlbumLocalFile = null;
            if (badge) badge.classList.add('hidden');
            const input = this.$refs.albumLocalFileInput;
            if (input) input.value = '';
            this.showToast((err && err.message) || '文件上传失败', 'error');
        });
    },

    saveNewAlbumMedia() {
        if (!this.requireLogin()) return;
        const titleInput = this.$refs.albumMediaTitleInput;
        const tagInput = this.$refs.albumMediaTagInput;
        const dateInput = this.$refs.albumMediaDateInput;
        const mediaUrlInput = this.$refs.albumMediaUrlInput;

        const title = titleInput ? titleInput.value.trim() : ''; // 素材标题/简介
        const typeid = tagInput ? tagInput.value : ''; // 主题分类字典id（option 的 value 为 albumList 的 id）
        const photoDate = (dateInput && dateInput.value) ? dateInput.value : new Date().toISOString().split('T')[0]; // 拍摄日期
        const mediaUrl = mediaUrlInput ? mediaUrlInput.value.trim() : ''; // 网络链接(可选)
        const files = this.uploadedAlbumLocalFile ? this.uploadedAlbumLocalFile.url : null; // 本地上传的图片/视频

        if (!title) {
            // 兜底：正常情况下表单 required 已由浏览器在输入框下方弹出提示
            const form = this.$refs.albumMediaForm;
            if (form && form.reportValidity) form.reportValidity();
            return;
        }

        let item = {
            'title': title,
            'typeid': typeid,
            'photoDate': photoDate,
            'mediaUrl': mediaUrl,
            'files': files
        }

        indexjs.addPhoto(item).then(res => {
            if (res.code === 200) {
                this.showToast("素材已存入成长相册！", 'success');
                // 保存成功后刷新相册数据（相册与日记是两份独立数据）
                this.loadAlbumMediaList();
            } else {
                this.showToast(res.msg || "保存失败！", 'error');
            }
        }).catch(err => {
            console.error('添加相册素材失败:', err);
        })

        // let url = mediaUrl || null;
        // let type = 'image';

        // if (this.uploadedAlbumLocalFile) {
        //     url = this.uploadedAlbumLocalFile.url;
        //     type = this.uploadedAlbumLocalFile.type;
        // }

        this.closeModal('addAlbumMediaModal');
        this.uploadedAlbumLocalFile = null;
        if (titleInput) titleInput.value = '';
        const badge = this.$refs.albumLocalFileBadge;
        if (badge) badge.classList.add('hidden');
    },

    // 从数据库查询相册素材（photo 表），刷新成长相册
    loadAlbumMediaList() {
        const container = this.$refs.albumGalleryContainer;
        if (!container) return;

        indexjs.getPhotoList({'pageNum': 1, 'pageSize': this.maxSize}).then(res => {
            if (res && res.code === 200) {
                // 只取有媒体链接的记录，转换为相册展示格式
                this.albumMediaList = (res.rows || []).filter(r => r.files || r.mediaUrl).map(r => {
                    const dict = this.albumList.find(d => Number(d.id) === Number(r.typeid));
                    const url = resolveMediaUrl(r.files || r.mediaUrl); // 相对地址拼接 BASE_URL
                    const type = /\.(mp4|mov|webm|avi)(\?.*)?$/i.test(url) ? 'video' : 'image';
                    const date = r.photoDate || '';
                    let yearMonth = '';
                    if (date) {
                        const [y, m] = date.split('-');
                        yearMonth = `${y}年${Number(m)}月`;
                    }
                    return {
                        id: r.id,
                        title: r.title,
                        tag: dict ? dict.dictName : '日常萌照',
                        typeid: r.typeid,
                        type: type,
                        date: date,
                        yearMonth: yearMonth,
                        url: url
                    };
                });
            }
            this.renderAlbumGallery();
        }).catch(err => {
            console.error('查询相册素材失败:', err);
            this.renderAlbumGallery();
        });
    },

    filterAlbumType(type) {
        this.currentAlbumType = type;
        this.renderAlbumGallery();
    },

    filterAlbumTag(tag) {
        this.currentAlbumTag = tag;
        this.renderAlbumGallery();
    },

    renderAlbumGallery() {
        const container = this.$refs.albumGalleryContainer;
        if (!container) return;

        let filtered = this.albumMediaList;
        if (this.currentAlbumType !== 'ALL') {
            filtered = filtered.filter(item => item.type === this.currentAlbumType);
        }
        if (this.currentAlbumTag !== 'ALL') {
            filtered = filtered.filter(item => Number(item.typeid) === Number(this.currentAlbumTag));
        }

        const photoEl = this.$refs.albumPhotoCount;
        const videoEl = this.$refs.albumVideoCount;
        if (photoEl) photoEl.innerText = this.albumMediaList.filter(i => i.type === 'image').length;
        if (videoEl) videoEl.innerText = this.albumMediaList.filter(i => i.type === 'video').length;

        if (filtered.length === 0) {
            container.innerHTML = `<div class="text-center text-xs text-slate-400 py-10">相册中暂无此类素材。</div>`;
            return;
        }

        const groups = {};
        filtered.forEach(item => {
            if (!groups[item.yearMonth]) groups[item.yearMonth] = [];
            groups[item.yearMonth].push(item);
        });

        container.innerHTML = Object.keys(groups).map(ym => `
            <div class="space-y-3">
                <div class="flex items-center space-x-2 border-b border-slate-200 pb-1.5">
                    <i class="fa-solid fa-camera text-teal-600 text-xs"></i>
                    <h4 class="font-extrabold text-xs text-slate-800 font-mono">${ym}</h4>
                    <span class="text-[10px] bg-teal-100 text-teal-800 px-2 py-0.2 rounded-full font-bold">${groups[ym].length} 个素材</span>
                </div>

                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    ${groups[ym].map(m => `
                        <div data-action="openMediaLightbox" data-id="${m.id}" class="group relative rounded-xl overflow-hidden bg-slate-900 border border-slate-200 shadow-2xs hover:shadow-md cursor-pointer transition h-40 flex items-center justify-center">
                            ${m.type === 'video' ? `
                                <video src="${m.url}" class="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition"></video>
                                <div class="absolute inset-0 flex items-center justify-center">
                                    <div class="w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center text-sm shadow-md group-hover:scale-110 transition">
                                        <i class="fa-solid fa-play ml-0.5"></i>
                                    </div>
                                </div>
                            ` : `
                                <img src="${m.url}" alt="${m.title}" class="w-full h-full object-cover group-hover:scale-105 transition duration-300">
                            `}
                            <div class="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 text-white space-y-0.5">
                                <span class="text-[9px] bg-teal-500/80 text-white px-1.5 py-0.2 rounded font-mono">${m.tag}</span>
                                <p class="text-[10px] font-bold truncate">${m.title}</p>
                            </div>
                        </div>
                    `).join('')}
                </div>
            </div>
        `).join('');
    },

    openMediaLightbox(id) {
        const m = this.albumMediaList.find(item => item.id === id);
        if (!m) return;

        this.$refs.lightboxTitle.innerText = m.title;
        this.$refs.lightboxTag.innerText = m.tag;
        this.$refs.lightboxDate.innerText = `拍摄时间: ${m.date}`;

        const img = this.$refs.lightboxImg;
        const vid = this.$refs.lightboxVideo;

        if (m.type === 'video') {
            img.classList.add('hidden');
            vid.src = m.url;
            vid.classList.remove('hidden');
        } else {
            vid.classList.add('hidden');
            img.src = m.url;
            img.classList.remove('hidden');
        }

        this.$refs.lightboxDeleteBtn.onclick = () => {
            this.deleteAlbumMedia(id);
            this.closeModal('mediaLightboxModal');
        };

        this.openModal('mediaLightboxModal');
    },

    deleteAlbumMedia(id) {
        if (!this.requireLogin()) return;
        if(!id) return;
        indexjs.deletePhoto(id).then(res => {
            if (res.code === 200) {
                this.showToast('删除成功', 'success');
            } else {
                this.showToast('删除失败', 'error');
            }
        });

        this.albumMediaList = this.albumMediaList.filter(item => item.id !== id);
        this.renderAlbumGallery();
    },

    // Food Plan
    switchFoodMonth(m) {
        this.currentFoodMonth = m;
        document.querySelectorAll('.food-month-btn').forEach(btn => {
            // 移除选中态样式
            btn.classList.remove('bg-amber-600', 'text-white', 'font-bold');
            // 还原未选中态样式
            btn.classList.add('bg-white', 'text-amber-900', 'border', 'border-amber-200', 'hover:bg-amber-100', 'font-medium');
        });
        const activeTab = this.$refs[`food-tab-${m}`];
        if (activeTab) {
            // 移除未选中态样式
            activeTab.classList.remove('bg-white', 'text-amber-900', 'border', 'border-amber-200', 'hover:bg-amber-100', 'font-medium');
            // 应用选中态样式
            activeTab.classList.add('bg-amber-600', 'text-white', 'font-bold');
        }
        this.renderFoodPlan();
    },

    toggleFoodCheck(id) {
        if (!this.requireLogin()) return;
        // 在所有月份中查找对应 id 的食谱项
        const months = this.foodPlanMonths;
        for (const m in months) {
            const list = months[m];
            if (!Array.isArray(list)) continue;
            const item = list.find(r => r.id == id);
            if (!item) continue;
            // 新值：选中为 1，未选中为 0
            const newVal = item.val == 1 ? 0 : 1;
            if (id > 0) {
                indexjs.updateFood({id: Number(id), val: newVal}).then(res => {
                    if (res && res.code == 200) {
                        item.val = newVal;
                        this.updateFoodCard(item);
                    }
                }).catch(() => {
                    this.showToast('更新辅食状态失败', 'error');
                });
            } else {
                // 数据库中尚无记录，仅本地切换
                item.val = newVal;
                this.updateFoodCard(item);
            }
            return;
        }
    },

    // 局部更新单个食谱卡片的选中展示状态
    updateFoodCard(item) {
        const card = this.$refs.foodMonthContent && this.$refs.foodMonthContent.querySelector(`[data-id="${item.id}"]`);
        if (!card) return;
        const checked = item.val == 1;
        card.classList.toggle('bg-amber-50', checked);
        card.classList.toggle('border-amber-400', checked);
        card.classList.toggle('bg-white', !checked);
        card.classList.toggle('border-amber-200', !checked);
        card.classList.toggle('opacity-90', checked);
        const checkbox = card.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = checked;
        card.querySelectorAll('.line-through').forEach(el => el.classList.remove('line-through'));
        if (checked) {
            card.querySelectorAll('span.font-extrabold, h5').forEach(el => el.classList.add('line-through'));
        }
    },

    renderFoodPlan() {
        const container = this.$refs.foodMonthContent;
        if (!container) return;

        const doRender = () => {
            const recipes = this.foodPlanMonths[this.currentFoodMonth] || [];
            container.innerHTML = `
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    ${recipes.map(r => `
                        <div data-action="toggleFoodCheck" data-id="${r.id}" class="${r.val == 1 ? 'bg-amber-50 border-amber-400' : 'bg-white border-amber-200'} border rounded-xl p-3 shadow-2xs space-y-1.5 flex items-start space-x-3 transition ${r.val == 1 ? 'opacity-90' : ''} cursor-pointer hover:border-amber-300">
                            <input type="checkbox" id="foodCheck-${r.id}" ${r.val == 1 ? 'checked' : ''} class="mt-1 rounded text-amber-600 focus:ring-amber-500 cursor-pointer">
                            <div class="space-y-0.5 flex-1 min-w-0">
                                <div class="flex justify-between items-center">
                                    <span class="font-extrabold text-amber-900 text-xs ${r.val == 1 ? 'line-through' : ''}">${r.dayRange}</span>
                                    <span class="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.2 rounded font-mono">${r.texture}</span>
                                </div>
                                <h5 class="font-bold text-xs text-slate-800 truncate ${r.val == 1 ? 'line-through text-slate-500' : ''}">🥣 食谱：${r.food}</h5>
                                <p class="text-[11px] text-slate-600 leading-tight">${r.note}</p>
                            </div>
                        </div>
                    `).join('')}
                </div>
            `;
        };

        indexjs.getFoodList({pageNum: 1, pageSize: 100}).then(res => {
            if (res && res.code == 200) {
                this.foodStatus = res.rows || [];
                if (this.foodStatus.length > 0) {
                    // 按顺序把 foodStatus 中每条数据的 id、val 赋给 foodPlanMonths 四组共 40 条数据
                    let idx = 0;
                    for (const m of Object.keys(this.foodPlanMonths)) {
                        const list = this.foodPlanMonths[m];
                        if (!Array.isArray(list)) continue;
                        for (const item of list) {
                            if (idx >= this.foodStatus.length) break;
                            item.id = this.foodStatus[idx].id;
                            item.val = this.foodStatus[idx].val;
                            idx++;
                        }
                        if (idx >= this.foodStatus.length) break;
                    }
                }
            }
            doRender();
        }).catch(() => {
            doRender();
        });
    },

    // Growth Charts Logic 保存 WHO生长曲线记录
    saveGrowthMeasurement(e) {
        if (!this.requireLogin()) return;
        e.preventDefault();
        const m = parseInt(this.$refs.entryMonth.value);
        const w = parseFloat(this.$refs.entryWeight.value);
        const h = parseFloat(this.$refs.entryHeight.value);
        const hc = parseFloat(this.$refs.entryHead.value) || null;

        // this.growthHistory.push({ month: m, weight: w, height: h, head: hc });

        let item = {
            'head':hc, // 头围
            'month':m, // 月龄
            'weight':w, // 体重
            'height':h, // 身高
            'assessment':'', // 评估结果
        }
        // 添加WHO生长曲线记录
        indexjs.addWho(item).then(res => {
            if (res && res.code === 200) {
                this.$refs.growthEntryForm.reset();
                // 保存成功后重新查询，列表与图表在数据返回后统一刷新
                this.renderGrowthRecords();
            }
        }).catch(err => {
            console.error('添加WHO生长记录失败:', err);
        });
    },

    deleteGrowthRecord(id) {
        if (!this.requireLogin()) return;
        if(id >0) {
            // 删除WHO生长曲线记录，成功后统一刷新（renderGrowthRecords 内部会刷新列表、图表与摘要）
            indexjs.deleteWho(id).then(res => {
                if (res && res.code === 200) {
                    this.renderGrowthRecords();
                }
            }).catch(err => {
                console.error('删除WHO生长记录失败:', err);
            });
        }
    },

    renderGrowthRecords() {
        const container = this.$refs.growthHistoryContainer;
        if (!container) return;

        indexjs.getWhoList({'pageNum':1, 'pageSize':this.maxSize}).then(res => {
            if (res && res.code === 200) {
                // rows 为多行，只提取 month、weight、height、head 转换为展示格式（id 用于删除）
                this.growthHistory = (res.rows || []).map(g => ({
                    month: g.month,
                    'weight': g.weight,
                    'height': g.height,
                    'head': g.head,
                    'id': g.id,
                }));
            }
            this.drawGrowthRecords();
            this.initGrowthCharts();
            this.updateGrowthSummary();
        }).catch(err => {
            console.error('查询WHO生长记录失败:', err);
            this.drawGrowthRecords();
        });
    },

    // 纯渲染：把当前 growthHistory 绘制到生长档案容器
    drawGrowthRecords() {
        const container = this.$refs.growthHistoryContainer;
        if (!container) return;

        if (this.growthHistory.length === 0) {
            container.innerHTML = `<div class="text-center text-xs text-slate-400 py-6">暂无生长测量数据记录。</div>`;
            return;
        }

        const sorted = [...this.growthHistory].sort((a, b) => b.month - a.month);

        container.innerHTML = sorted.map(g => `
            <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-2">
                <div class="flex justify-between items-center border-b border-slate-100 pb-2">
                    <div class="flex items-center space-x-2">
                        <span class="font-extrabold text-xs sm:text-sm text-slate-800">${g.month} 月龄</span>
                        <span class="text-[10px] bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-mono font-bold">WHO测量档案</span>
                    </div>
                    <button data-action="deleteGrowthRecord" data-id="${g.id}" class="text-slate-400 hover:text-rose-600 text-xs transition" title="删除此条记录">
                        <i class="fa-solid fa-trash-can"></i>
                    </button>
                </div>

                <div class="grid grid-cols-3 gap-2 text-[11px] text-slate-600 font-mono bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                    <div>⚖️ 体重: <strong class="text-slate-800">${g.weight} kg</strong></div>
                    <div>📏 身高: <strong class="text-slate-800">${g.height} cm</strong></div>
                    <div>🧠 头围: <strong class="text-slate-800">${g.head ? g.head + ' cm' : '未测量'}</strong></div>
                </div>

                <div class="text-xs text-slate-700 leading-relaxed bg-purple-50/40 p-2.5 rounded-lg border border-purple-100/60">
                    <span class="font-bold text-purple-900">📊 发育测评：</span>
                    <span class="text-slate-700">体重与身高处于 WHO P50 黄金中位数水平附近，发育曲线平稳，整体指标优秀。</span>
                </div>
            </div>
        `).join('');
    },

    updateGrowthSummary() {
        if (this.growthHistory.length === 0) return;
        // 按 month 升序排序，取最大月龄（最近一次测量）的记录
        const sorted = [...this.growthHistory].sort((a, b) => a.month - b.month);
        const latest = sorted[sorted.length - 1];
        const tagEl = this.$refs.latestMonthTag;
        if (tagEl) {
          tagEl.innerText = `最新月龄: ${latest.month}个月`;  
          this.latestMonth = latest.month;
        }

        const summaryEl = this.$refs.growthSummaryText;
        if (summaryEl) {
            summaryEl.innerHTML = `
                ${this.userInfo.chlidName || this.babyName}在 <strong>${latest.month} 月龄</strong> 测量：体重 <strong>${latest.weight} kg</strong>，身高 <strong>${latest.height} cm</strong>。<br>
                根据 WHO 0-3岁男孩百分位数标准：体重处于 <strong>P50 黄金中位数水平</strong>，生长发育曲线平稳上升，营养摄入充足，奶爸护理效果极佳！
            `;
        }
    },

    initGrowthCharts() {
        const wCanvas = this.$refs.weightChartCanvas;
        const hCanvas = this.$refs.heightChartCanvas;
        if (!wCanvas || !hCanvas) return;

        // 身高和体重数据
        const userWeightData = this.p50months.map(m => {
            const found = this.growthHistory.find(g => g.month === m);
            return found ? found.weight : null;
        });

        const userHeightData = this.p50months.map(m => {
            const found = this.growthHistory.find(g => g.month === m);
            return found ? found.height : null;
        });

        if (weightChart) weightChart.destroy();
        weightChart = new Chart(wCanvas, {
            type: 'line',
            data: {
                labels: this.p50months.map(m => `${m}月`),
                datasets: [
                    { label: '秉宣测量体重(kg)', data: userWeightData, borderColor: '#0284c7', backgroundColor: '#0284c7', borderWidth: 3, pointRadius: 5 },
                    { label: 'WHO P50 标准线', data: this.p50Weight, borderColor: '#cbd5e1', borderDash: [5, 5], fill: false }
                ]
            },
            options: { responsive: true, maintainAspectRatio: false }
        });

        if (heightChart) heightChart.destroy();
        heightChart = new Chart(hCanvas, {
            type: 'line',
            data: {
                labels: this.p50months.map(m => `${m}月`),
                datasets: [
                    { label: '秉宣测量身高(cm)', data: userHeightData, borderColor: '#9333ea', backgroundColor: '#9333ea', borderWidth: 3, pointRadius: 5 },
                    { label: 'WHO P50 标准线', data: this.p50Height, borderColor: '#cbd5e1', borderDash: [5, 5], fill: false }
                ]
            },
            options: { responsive: true, maintainAspectRatio: false }
        });
    },

    // 事件委托：统一处理动态 innerHTML 中带 data-action 的点击
    delegateClick(event) {
        const trigger = event.target.closest('[data-action]');
        if (!trigger) return;
        const action = trigger.dataset.action;
        const rawId = trigger.dataset.id;
        const id = rawId === undefined ? undefined : (isNaN(Number(rawId)) ? rawId : Number(rawId));
        switch (action) {
            case 'deleteMoodPost': this.deleteMoodPost(id); break;
            case 'toggleLikeMood': this.toggleLikeMood(id); break;
            case 'deleteFetalRecord': this.deleteFetalRecord(id); break;
            case 'deleteTrackerLog': this.deleteTrackerLog(id); break;
            case 'openDiaryDetail': this.openDiaryDetail(id); break;
            case 'openMediaLightbox': this.openMediaLightbox(id); break;
            case 'toggleFoodCheck': this.toggleFoodCheck(id); break;
            case 'deleteGrowthRecord': this.deleteGrowthRecord(id); break;
            default: break;
        }
    },

    getUserInfo() {
        indexjs.getUserInfo().then(res => {
            if(res && res.code === 200) {
                this.userInfo = res.data || {};
                this.applyUserInfo();
            }
        })
    },

    // 将 userInfo 中的配置应用到页面展示
    applyUserInfo() {
        const u = this.userInfo || {};
        if (u.webTitle) this.siteTitle = u.webTitle;           // 网站标题
        if (u.fatherName) this.dadName = u.fatherName;         // 奶爸昵称
        if (u.chlidName) this.babyName = u.chlidName;          // 宝宝姓名
        if (u.fatherTag) this.dadStatus = u.fatherTag;         // 奶爸状态标签
        if (u.fatherImage) {
            this.avatarPath = u.fatherImage;                   // 相对路径（存库用）
            this.avatarUrl = resolveMediaUrl(u.fatherImage);   // 奶爸头像（展示用）
        }

        // 选择移植 / 月经日期
        if (u.yizhidate) {
            this.transferDate = u.yizhidate;
            const dateInput = this.$refs.calcDateInput;
            if (dateInput) dateInput.value = u.yizhidate;
        }
        // 选择推算依据（存的是 pregnancyList 字典 id）
        if (u.yuchanqituisuan) {
            this.embryoType = u.yuchanqituisuan;
            const typeSelect = this.$refs.calcTypeSelect;
            if (typeSelect) typeSelect.value = String(u.yuchanqituisuan);
            // 字典已加载时按"+ N天"推算预产期
            const dict = this.pregnancyList.find(d => Number(d.id) === Number(u.yuchanqituisuan));
            const match = dict ? dict.dictName.match(/\+\s*(\d+)\s*天/) : null;
            if (match && u.yizhidate) {
                const baseDate = new Date(u.yizhidate);
                baseDate.setDate(baseDate.getDate() + Number(match[1]));
                const yyyy = baseDate.getFullYear();
                const mm = String(baseDate.getMonth() + 1).padStart(2, '0');
                const dd = String(baseDate.getDate()).padStart(2, '0');
                this.dueDate = `${yyyy}-${mm}-${dd}`;
                this.updateCountdown();
            }
        }

        this.updateProfileUI();
    },

    initApp() {
        try {
            // 加载数据字典
            indexjs.getOrgtreeList({"dicNames":"日常护理,时光成长日记,时光成长相册,心情微语,预产期推算算法"}).then(res => {
            
                // 设置数据字典
                this.orgtreeList = res || [];
                if(this.orgtreeList.length > 0) {
                    this.dailyList = this.orgtreeList[0] || [];
                }
                if(this.orgtreeList.length > 1) {
                    this.diaryList = this.orgtreeList[1] || [];
                }
                if(this.orgtreeList.length > 2) {
                    this.albumList = this.orgtreeList[2] || [];
                }
                if(this.orgtreeList.length > 3) {
                    this.moodList = this.orgtreeList[3] || [];
                }
                if(this.orgtreeList.length > 4) {
                    this.pregnancyList = this.orgtreeList[4] || [];
                }

                // 字典可能晚于用户信息加载完成，重新应用一次以回填推算依据下拉框
                if (this.userInfo && Object.keys(this.userInfo).length > 0) {
                    this.applyUserInfo();
                }
            })

            // 拉取用户信息
            this.getUserInfo();

            // 拉取全部日常护理打卡记录并渲染 （默认首页）
            this.refreshTrackerLogs();

            // 刷新 秉宣诞生倒计时等信息
            this.updateProfileUI();
        } catch (err) {
            console.error("Initialization error:", err);
        }
    }
    },
    mounted() {
        // 页面初始化即同步一次浏览器标题（watch 只在值变化时触发）
        document.title = this.siteTitle;
        // 等 Vue 把模板渲染到 DOM 后再初始化
        this.$nextTick(() => {
            this.initApp();
        });
    }
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}
html, body 
{
    height: 100%;
    font-family: 'Noto Sans SC', sans-serif;
    background-color: #f8fafc;
    color: #1e293b;
    overflow-x: hidden;
}
.nav-item.active {
    background-color: #e0f2fe;
    color: #0369a1;
    font-weight: 700;
    border-right: 4px solid #0284c7;
}
.nav-item.active i {
    color: #0284c7 !important;
}
.custom-scrollbar::-webkit-scrollbar {
    width: 5px;
    height: 5px;
}
.custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 9999px;
}

.bkleft {
    float: left;
}
.txtleft, label,div,span,p {
    text-align: left;
}
</style>
